import { ApiClient } from '../services/ApiClient.js';
import { Notification } from '../services/Notification.js';
import { Storage } from '../services/Storage.js';
import { translate } from './i18n.js';
import { EventEditor } from './EventEditor.js';
import { EventReport } from './EventReport.js';

const SUPPORTED_DISCIPLINES = ['kendo', 'iaido', 'jodo', 'kyudo', 'naginata', 'karate-do', 'battodo', 'chanbara'];
const DEFAULT_DISCIPLINES = ['kendo', 'iaido', 'jodo', 'kyudo', 'naginata', 'battodo', 'chanbara'];
const AGENDA_BATCH_SIZE = 50;
const TIMELINE_MARKER_OVERSCAN_DAYS = 21;

export class Events {
    constructor(app, options = {}) {
        this.app = app;
        this.api = options.api || app?.api || app?.account?.api || new ApiClient(this.getApiConfig(app), app);
        this.storage = options.storage || Storage;
        this.locale = app.locale || document.documentElement.lang || 'fr';
        this.events = [];
        this.loaded = false;
        this.loadingPromise = null;
        this.lastLoadError = null;
        this.viewContainers = new Map();
        this.dirtyViews = new Set();
        this.firstDate = null;
        this.lastDate = null;
        this.currentDate = this.getToday();
        this.selectedDate = this.getToday();
        this.focusedYear = this.selectedDate.getFullYear();
        this.filters = {
            disciplines: new Set(),
            additionalTags: new Set(),
            countries: new Set()
        };
        this.filtersOpen = false;
        this.expandedEventKeys = new Set();
        this.validationEventUid = null;
        this.publicationValidationInProgress = false;
        this.pendingOrganisationDetails = null;
        this.activeOrganisationModal = null;
        this.dateScrollTimer = null;
        this.dateScrollFrame = null;
        this.pendingDateScrollBehavior = 'auto';
        this.timelineInitialized = false;
        this.timelineResizeObserver = null;
        this.timelineDimensionUpdate = null;
        this.timelineMarkerStates = new WeakMap();
        this.timelineMarkerCleanup = null;
        this.agendaRenderStates = new WeakMap();
        this.activeAgendaList = null;
        this.container = options.container || document.getElementById('calendrier');
        this.maxVisibleEventsPerDay = options.maxVisibleEventsPerDay || 4;
        this.disciplines = this.getConfiguredDisciplines();
        this.disciplineLabels = {
            'kendo': 'Kendo',
            'iaido': 'Iaido',
            'jodo': 'Jodo',
            'kyudo': 'Kyudo',
            'naginata': 'Naginata',
            'karate-do': 'Karate-do',
            'battodo': 'Battodo',
            'chanbara': 'Chanbara'
        };
        this.disciplineColorVariables = {
            'kendo': '--kendo-color',
            'iaido': '--iaido-color',
            'jodo': '--jodo-color',
            'kyudo': '--kyudo-color',
            'naginata': '--naginata-color',
            'karate-do': '--karatedo-color',
            'battodo': '--battodo-color',
            'chanbara': '--chanbara-color'
        };
        this.editor = new EventEditor(this);
        this.reporter = new EventReport(this.app, { api: this.api });
        if (this.app?.simpleModal) {
            Object.assign(
                this.app.simpleModal.contents,
                this.getContents(),
                this.editor.getContents(),
                this.reporter.getContents()
            );
        }
    }

    getContents() {
        return {
            eventDetails: {
                titleKey: 'eventModal.dialog',
                icon: '/images/calendar-grey.svg',
                header: false,
                render: (context) => this.renderEventDetails(context),
                cleanup: () => this.cleanupEventDetails()
            },
            organisationDetails: {
                titleKey: 'organizationModal.dialog',
                icon: '/images/account-grey.svg',
                header: false,
                render: (context) => this.renderOrganisationDetails(context),
                cleanup: () => this.cleanupOrganisationDetails()
            }
        };
    }

    async restoreFilters() {
        try {
            const storedFilters = await this.storage.getEventFilters();

            if (!storedFilters || typeof storedFilters !== 'object') {
                return false;
            }

            Object.keys(this.filters).forEach((filterType) => {
                const values = storedFilters[filterType];
                const restoredValues = Array.isArray(values)
                    ? values.filter((value) => typeof value === 'string' && value.length > 0)
                    : [];

                this.filters[filterType] = new Set(
                    filterType === 'disciplines'
                        ? restoredValues.filter((value) => this.disciplines.includes(value))
                        : restoredValues
                );
            });

            return true;
        } catch (error) {
            console.warn('Unable to restore event filters from local storage.', error);
            return false;
        }
    }

    async persistFilters() {
        const storedFilters = Object.fromEntries(
            Object.entries(this.filters).map(([filterType, values]) => [filterType, [...values]])
        );

        try {
            await this.storage.setEventFilters(storedFilters);
            return true;
        } catch (error) {
            console.warn('Unable to save event filters to local storage.', error);
            return false;
        }
    }

    getApiConfig(app) {
        const apiConfig = app.config?.api || {};

        return {
            ...apiConfig,
            publicToken: apiConfig.publicToken || apiConfig.generic_token || 'PUBLIC'
        };
    }

    getConfiguredDisciplines() {
        const configured = this.app?.config?.calendar?.disciplines;
        const disciplines = Array.isArray(configured) ? configured : DEFAULT_DISCIPLINES;

        return [...new Set(disciplines
            .map((discipline) => String(discipline).trim().toLowerCase())
            .filter((discipline) => SUPPORTED_DISCIPLINES.includes(discipline)))];
    }

    translate(key, parameters = {}) {
        return translate(key, this.locale, parameters);
    }

    add() {
        if (!this.requireAuthentication()) {
            return false;
        }

        return this.editor.open();
    }

    subscribe(returnFocus = null) {
        return this.app?.calendarSubscriptions?.open(returnFocus) || false;
    }

    requireAuthentication() {
        if (this.app?.user) {
            return true;
        }

        new Notification({
            icon: 'signin-grey.svg',
            title: this.translate('auth.requiredTitle'),
            content: this.translate('auth.requiredMessage')
        });

        return false;
    }

    async list(params = {}, lifecycle = {}) {
        if (this.loadingPromise) {
            return this.waitForCurrentLoad(lifecycle);
        }

        const requestParams = {
            locale: this.locale,
            ...(params && typeof params === 'object' ? params : {})
        };

        this.loadingPromise = new Promise((resolve) => {
            let settled = false;
            const settle = (events) => {
                if (settled) return;
                settled = true;
                resolve(events);
            };

            this.api.query(
                'get',
                '/events/list',
                requestParams,
                () => {
                    this.callRequestLifecycle(lifecycle.before);
                },
                (response) => {
                    this.events = response?.result?.events || [];
                    this.loaded = true;
                    this.lastLoadError = null;
                    this.renderMountedViews();
                    this.callRequestLifecycle(lifecycle.success, this.events, response);
                    settle(this.events);
                },
                null,
                {
                    onError: (error) => {
                        this.loaded = false;
                        this.lastLoadError = error || this.api.lastError || null;
                        this.renderError();
                        this.callRequestLifecycle(lifecycle.error, this.lastLoadError);
                        settle(null);
                    }
                }
            ).catch((error) => {
                this.loaded = false;
                this.lastLoadError = error;
                this.renderError();
                this.callRequestLifecycle(lifecycle.error, error);
                settle(null);
            });
        });

        try {
            return await this.loadingPromise;
        } finally {
            this.loadingPromise = null;
        }
    }

    async ensureLoaded(params = {}, lifecycle = {}) {
        if (this.loaded) {
            return this.events;
        }

        if (this.loadingPromise) {
            return this.waitForCurrentLoad(lifecycle);
        }

        return this.list(params, lifecycle);
    }

    async waitForCurrentLoad(lifecycle = {}) {
        this.callRequestLifecycle(lifecycle.before);
        const events = await this.loadingPromise;

        if (this.loaded) {
            this.callRequestLifecycle(lifecycle.success, events, null);
        } else {
            this.callRequestLifecycle(lifecycle.error, this.lastLoadError);
        }

        return events;
    }

    callRequestLifecycle(callback, ...args) {
        if (typeof callback !== 'function') {
            return false;
        }

        try {
            callback(...args);
            return true;
        } catch (error) {
            console.error('Unable to run an events request lifecycle callback.', error);
            return false;
        }
    }

    setDateBounds(firstDate = null, lastDate = null) {
        this.firstDate = this.parseDate(firstDate);
        this.lastDate = this.parseDate(lastDate);
    }

    mountView(mode, host) {
        if (!host) return false;

        const container = document.createElement('section');

        container.className = `calendar home-events-view home-events-view-${mode}`;
        host.replaceChildren(container);
        this.viewContainers.set(mode, container);
        this.renderView(mode, container);
        this.dirtyViews.delete(mode);

        return container;
    }

    activateView(mode) {
        const container = this.viewContainers.get(mode);

        if (!container?.isConnected) {
            return false;
        }

        if (this.dirtyViews.has(mode) && this.renderView(mode, container)) {
            this.dirtyViews.delete(mode);
        }
        this.container = container;
        if (mode === 'calendar') {
            window.requestAnimationFrame(() => {
                this.timelineDimensionUpdate?.();
            });
        }

        return true;
    }

    renderMountedViews() {
        this.viewContainers.forEach((container, mode) => {
            if (!container.isConnected) {
                this.viewContainers.delete(mode);
                this.dirtyViews.delete(mode);
                return;
            }

            const viewPanel = container.closest('[data-home-view-panel]');

            if (viewPanel?.hidden) {
                this.dirtyViews.add(mode);
                return;
            }

            this.renderView(mode, container);
            this.dirtyViews.delete(mode);
        });
        this.app?.home?.renderUpcomingEvents?.();
    }

    renderView(mode, container) {
        if (!container) return false;

        if (mode === 'calendar') {
            this.timelineResizeObserver?.disconnect();
            this.timelineResizeObserver = null;
            this.timelineDimensionUpdate = null;
            this.timelineMarkerCleanup?.();
            this.timelineTooltipCleanup?.();
        }
        if (mode === 'list') {
            this.cleanupAgendaProgressiveLoading(container.querySelector('.calendar-agenda'));
        }
        container.replaceChildren();

        if (mode === 'list') {
            const agenda = this.createAgendaList({
                includeOrganization: false,
                mergeTagGroups: true
            });

            this.container = container;
            container.append(agenda);
            this.initializeAgendaProgressiveLoading(container, agenda);
            return true;
        }

        if (mode === 'calendar') {
            const today = this.getToday();

            this.container = container;
            this.selectedDate = today;
            this.focusedYear = today.getFullYear();
            container.append(this.createDateNavigator());
            window.requestAnimationFrame(() => {
                this.scrollMonthIntoView(today, 'auto');
            });
            return true;
        }

        if (mode === 'filters') {
            const panel = this.createFiltersPanel();

            panel.hidden = false;
            panel.classList.add('open', 'home-filters-panel');
            container.append(panel);
            return true;
        }

        return false;
    }

    render() {
        if (!this.container) {
            return false;
        }

        this.timelineResizeObserver?.disconnect();
        this.timelineResizeObserver = null;
        this.timelineDimensionUpdate = null;
        this.timelineMarkerCleanup?.();
        this.timelineTooltipCleanup?.();
        this.container.innerHTML = '';
        this.normalizeSelectedDate();
        this.container.append(this.createCalendarInterface());
        const initialTimelineScroll = !this.timelineInitialized;

        this.timelineInitialized = true;

        window.requestAnimationFrame(() => {
            if (initialTimelineScroll) {
                this.scrollMonthIntoView(this.getToday(), this.pendingDateScrollBehavior);
            } else {
                this.scrollSelectedDateIntoView(this.pendingDateScrollBehavior);
            }
            this.pendingDateScrollBehavior = 'auto';
            this.scheduleSelectedDateAlignment(initialTimelineScroll ? 'month' : 'day');
        });

        return true;
    }

    renderError() {
        if (!this.container) {
            return false;
        }

        this.timelineResizeObserver?.disconnect();
        this.timelineResizeObserver = null;
        this.timelineDimensionUpdate = null;
        this.timelineMarkerCleanup?.();
        this.timelineTooltipCleanup?.();
        this.container.innerHTML = '';

        const message = document.createElement('div');
        message.className = 'calendar-error';
        message.textContent = 'Impossible de charger les évènements.';
        this.container.append(message);

        return true;
    }

    createCalendarInterface() {
        const interfaceElement = document.createElement('div');
        const agenda = this.createAgendaList();

        interfaceElement.className = 'calendar-interface';
        interfaceElement.append(
            this.createCalendarControls(),
            this.createDateNavigator(),
            agenda
        );

        return interfaceElement;
    }

    createCalendarControls() {
        const controls = document.createElement('div');

        controls.className = 'calendar-controls';
        controls.append(this.createCalendarTopbar(), this.createFiltersPanel());

        return controls;
    }

    createCalendarTopbar() {
        const topbar = document.createElement('div');

        topbar.className = 'calendar-topbar';
        topbar.append(this.createFilterToggle());

        return topbar;
    }

    createFilterToggle() {
        const button = document.createElement('button');
        const content = document.createElement('span');
        const count = this.getActiveFilterCount();
        const createState = (name, iconSource, translationKey) => {
            const state = document.createElement('span');
            const icon = document.createElement('img');
            const label = document.createElement('span');

            state.className = `calendar-filter-toggle-state calendar-filter-toggle-state-${name}`;
            state.setAttribute('aria-hidden', 'true');
            icon.src = iconSource;
            icon.alt = '';
            icon.loading = 'lazy';
            label.className = 'calendar-filter-toggle-label';
            label.textContent = this.translate(translationKey);
            state.append(icon, label);

            return state;
        };

        button.type = 'button';
        button.className = 'calendar-filter-toggle ui-buttons medium square';
        button.setAttribute('aria-controls', 'calendar-filters');
        button.setAttribute('aria-expanded', String(Boolean(this.filtersOpen)));
        button.setAttribute('aria-label', this.translate(this.filtersOpen ? 'filters.close' : 'filters.button'));
        content.className = 'calendar-filter-toggle-content';
        content.append(
            createState('filters', '/images/filter-grey.svg', 'filters.button'),
            createState('close', '/images/close-grey.svg', 'filters.close')
        );
        button.append(content);
        if (count > 0) {
            const badge = document.createElement('span');

            badge.className = 'calendar-filter-count';
            badge.textContent = String(count);
            badge.setAttribute('aria-label', this.translate('filters.activeCount', { count }));
            button.append(badge);
        }
        button.addEventListener('click', () => {
            this.filtersOpen = !this.filtersOpen;
            this.setFiltersPanelOpen(button, this.filtersOpen);
        });

        return button;
    }

    createFiltersPanel() {
        const panel = document.createElement('div');

        panel.id = 'calendar-filters';
        panel.className = 'calendar-filters';
        panel.hidden = !this.filtersOpen;
        panel.classList.toggle('open', this.filtersOpen);

        panel.append(
            this.createTagFilterSection(
                this.translate('filters.disciplines'),
                this.getDisciplineFilterTags(),
                'disciplines'
            ),
            this.createTagFilterSection(
                this.translate('filters.additionalTags'),
                this.getAdditionalFilterTags(),
                'additionalTags'
            ),
            this.createTagFilterSection(
                this.translate('filters.countries'),
                this.getCountryFilterTags(),
                'countries'
            ),
            this.createFilterResultsCount()
        );

        return panel;
    }

    setFiltersPanelOpen(button, isOpen) {
        const panel = button.closest('.calendar-controls')?.querySelector('.calendar-filters');

        if (!panel) {
            return false;
        }

        this.updateFilterToggle(button, isOpen);
        window.clearTimeout(this.filtersCloseTimer);

        if (isOpen) {
            panel.hidden = false;
            panel.classList.remove('closing');
            panel.getBoundingClientRect();
            window.requestAnimationFrame(() => panel.classList.add('open'));
            return true;
        }

        panel.classList.remove('open');
        panel.classList.add('closing');

        const finishClosing = () => {
            if (!this.filtersOpen) {
                panel.hidden = true;
                panel.classList.remove('closing');
            }
        };

        panel.addEventListener('transitionend', (event) => {
            if (event.target === panel) {
                finishClosing();
            }
        }, { once: true });
        this.filtersCloseTimer = window.setTimeout(finishClosing, 450);

        return true;
    }

    updateFilterToggle(button, isOpen) {
        button.setAttribute('aria-expanded', String(isOpen));
        button.setAttribute('aria-label', this.translate(isOpen ? 'filters.close' : 'filters.button'));
    }

    createTagFilterSection(title, tags, filterType) {
        const section = document.createElement('section');
        const heading = document.createElement('h3');
        const list = document.createElement('div');
        const activeValues = this.filters[filterType];

        section.className = 'calendar-filter-section';
        heading.className = 'calendar-filter-title';
        heading.textContent = title;
        list.className = 'calendar-filter-tags';

        tags.forEach(({ id, label, color }) => {
            const button = document.createElement('button');

            button.type = 'button';
            button.className = 'calendar-filter-tag';
            button.dataset.filter = filterType;
            button.dataset.value = id;
            button.textContent = label;
            button.style.setProperty('--calendar-filter-tag-color', color || '#667481');
            button.setAttribute('aria-pressed', String(activeValues.has(id)));
            if (activeValues.has(id)) {
                button.classList.add('active');
            }
            button.addEventListener('click', () => this.toggleFilterValue(filterType, id, button));
            list.append(button);
        });

        if (tags.length === 0) {
            section.hidden = true;
        }

        section.append(heading, list);

        return section;
    }

    createFilterResultsCount() {
        const indicator = document.createElement('p');
        const count = this.getFilteredEvents().length;
        const countMarker = '__EVENTS_COUNT__';
        const message = this.translate(
            count === 1 ? 'filters.resultOne' : 'filters.resultsMany',
            { count: countMarker }
        );
        const [beforeCount, afterCount = ''] = message.split(countMarker);
        const countElement = document.createElement('span');

        indicator.className = 'calendar-filter-results';
        indicator.setAttribute('role', 'status');
        indicator.setAttribute('aria-live', 'polite');
        countElement.className = 'calendar-filter-results-count';
        countElement.textContent = String(count);
        indicator.append(
            document.createTextNode(beforeCount),
            countElement,
            document.createTextNode(afterCount)
        );

        return indicator;
    }

    createDateNavigator() {
        const shell = document.createElement('div');
        const navigator = document.createElement('div');
        const scrollers = {
            year: document.createElement('div'),
            month: document.createElement('div'),
            day: document.createElement('div')
        };
        const tracks = {
            year: document.createElement('div'),
            month: document.createElement('div'),
            day: document.createElement('div')
        };
        const years = document.createElement('div');
        const months = document.createElement('div');
        const daysRow = document.createElement('div');
        const tooltip = this.createTimelineEventTooltip();
        const buttons = {
            year: {
                previous: this.createTimelineScrollButton('previous', 'year'),
                next: this.createTimelineScrollButton('next', 'year')
            },
            month: {
                previous: this.createTimelineScrollButton('previous', 'month'),
                next: this.createTimelineScrollButton('next', 'month')
            },
            day: {
                previous: this.createTimelineScrollButton('previous', 'day'),
                next: this.createTimelineScrollButton('next', 'day')
            }
        };
        const days = this.getDateNavigatorDays();
        const filteredEvents = this.getFilteredEvents();
        const eventsByDate = this.getEventOccurrencesByDate(filteredEvents);
        const timelineLayout = this.getTimelineEventLayout(filteredEvents);
        const markerBlockHeight = 15;
        const markerGap = 5;
        const markerPitch = markerBlockHeight + markerGap;
        const markersHeight = (Math.max(timelineLayout.laneCount, 1) * markerPitch) - markerGap;
        const dayHeight = 29 + markersHeight;
        const timelineHeight = 19 + 17 + dayHeight;
        const seenYears = new Set();
        const seenMonths = new Set();

        shell.className = 'calendar-timeline-shell';
        navigator.className = 'calendar-timeline';
        years.className = 'calendar-timeline-row calendar-timeline-years';
        months.className = 'calendar-timeline-row calendar-timeline-months';
        daysRow.className = 'calendar-timeline-row calendar-timeline-days';
        navigator.style.setProperty('--calendar-timeline-day-row-height', `${dayHeight}px`);
        shell.style.height = `${timelineHeight}px`;
        navigator.style.height = `${timelineHeight}px`;
        Object.entries(scrollers).forEach(([row, scroller]) => {
            scroller.className = `calendar-timeline-scroller calendar-timeline-scroller-${row}s`;
            scroller.dataset.timelineRow = row;
            tracks[row].className = `calendar-timeline-track calendar-timeline-track-${row}s`;
        });

        days.forEach((date) => {
            const yearKey = String(date.getFullYear());
            const monthKey = this.formatDateKey(date).slice(0, 7);

            if (!seenYears.has(yearKey)) {
                seenYears.add(yearKey);
                years.append(this.createTimelinePeriod(
                    'year',
                    this.formatDateYear(date),
                    date
                ));
            }
            if (!seenMonths.has(monthKey)) {
                seenMonths.add(monthKey);
                months.append(this.createTimelinePeriod(
                    'month',
                    this.formatDateMonth(date),
                    date
                ));
            }

            daysRow.append(this.createTimelineDay(
                date,
                eventsByDate.get(this.formatDateKey(date)) || [],
                dayHeight
            ));
        });
        const markers = this.createTimelineEventMarkers(
            filteredEvents,
            days,
            timelineLayout.lanes,
            markersHeight,
            markerBlockHeight,
            markerGap
        );

        daysRow.append(markers);

        tracks.year.append(years);
        tracks.month.append(months);
        tracks.day.append(daysRow);
        scrollers.year.append(tracks.year);
        scrollers.month.append(tracks.month);
        scrollers.day.append(tracks.day);
        navigator.append(
            scrollers.year,
            scrollers.month,
            scrollers.day,
            buttons.year.previous,
            buttons.year.next,
            buttons.month.previous,
            buttons.month.next,
            buttons.day.previous,
            buttons.day.next
        );
        shell.append(navigator, tooltip);
        const updateMarkerWindow = this.bindTimelineEventVirtualization(scrollers.day, markers);

        this.bindTimelineNavigation(navigator, scrollers, buttons, {
            onDayGeometryChange: updateMarkerWindow
        });
        this.bindTimelineEventTooltip(shell, navigator, tooltip);

        return shell;
    }

    createTimelineEventTooltip() {
        const tooltip = document.createElement('div');
        const title = document.createElement('span');
        const discipline = document.createElement('span');
        const hint = document.createElement('span');

        tooltip.className = 'calendar-timeline-event-tooltip';
        tooltip.setAttribute('role', 'tooltip');
        tooltip.hidden = true;
        title.className = 'calendar-timeline-event-tooltip-title';
        discipline.className = 'calendar-timeline-event-tooltip-discipline';
        hint.className = 'calendar-timeline-event-tooltip-hint';
        hint.textContent = this.translate('timeline.clickForMoreInfo');
        tooltip.append(title, discipline, hint);

        return tooltip;
    }

    bindTimelineEventTooltip(shell, navigator, tooltip) {
        const title = tooltip.querySelector('.calendar-timeline-event-tooltip-title');
        const discipline = tooltip.querySelector('.calendar-timeline-event-tooltip-discipline');
        const hide = () => {
            tooltip.hidden = true;
            delete tooltip.dataset.eventId;
            navigator.querySelectorAll('.calendar-timeline-marker.is-hovered').forEach((marker) => {
                marker.classList.remove('is-hovered');
            });
        };
        const show = (marker, event) => {
            navigator.querySelectorAll('.calendar-timeline-marker.is-hovered').forEach((hoveredMarker) => {
                if (hoveredMarker !== marker) {
                    hoveredMarker.classList.remove('is-hovered');
                }
            });
            if (tooltip.dataset.eventId !== marker.dataset.eventId) {
                tooltip.dataset.eventId = marker.dataset.eventId;
                title.textContent = marker.dataset.eventTitle || '';
                discipline.textContent = marker.dataset.eventDiscipline || '';
                discipline.hidden = !discipline.textContent;
                tooltip.style.setProperty(
                    '--calendar-timeline-tooltip-color',
                    marker.dataset.eventColor || 'rgb(0 0 0)'
                );
            }

            marker.classList.add('is-hovered');
            tooltip.hidden = false;
            const shellRect = shell.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const offset = 14;
            const viewportMargin = 8;
            let viewportLeft = event.clientX + offset;
            let viewportTop = event.clientY + offset;

            if (viewportLeft + tooltipRect.width > window.innerWidth - viewportMargin) {
                viewportLeft = event.clientX - tooltipRect.width - offset;
            }
            if (viewportTop + tooltipRect.height > window.innerHeight - viewportMargin) {
                viewportTop = event.clientY - tooltipRect.height - offset;
            }

            viewportLeft = Math.max(viewportMargin, viewportLeft);
            viewportTop = Math.max(viewportMargin, viewportTop);
            tooltip.style.left = `${viewportLeft - shellRect.left}px`;
            tooltip.style.top = `${viewportTop - shellRect.top}px`;
        };
        const handlePointerMove = (event) => {
            const marker = event.target.closest('.calendar-timeline-marker');

            if (marker) {
                show(marker, event);
            }
        };
        const handlePointerOut = (event) => {
            const marker = event.target.closest('.calendar-timeline-marker');

            if (marker && !marker.contains(event.relatedTarget)) {
                marker.classList.remove('is-hovered');
                hide();
            }
        };
        const cleanup = () => {
            navigator.removeEventListener('pointermove', handlePointerMove);
            navigator.removeEventListener('pointerout', handlePointerOut);
            navigator.removeEventListener('pointerleave', hide);
            if (this.timelineTooltipCleanup === cleanup) {
                this.timelineTooltipCleanup = null;
            }
        };

        this.timelineTooltipCleanup?.();
        this.timelineTooltipCleanup = cleanup;
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            return;
        }

        navigator.addEventListener('pointermove', handlePointerMove);
        navigator.addEventListener('pointerout', handlePointerOut);
        navigator.addEventListener('pointerleave', hide);
    }

    createTimelineScrollButton(direction, row) {
        const button = document.createElement('button');
        const icon = document.createElement('img');

        button.type = 'button';
        button.className = [
            'calendar-timeline-scroll-button',
            `calendar-timeline-scroll-${direction}`,
            `calendar-timeline-scroll-${row}`
        ].join(' ');
        button.setAttribute('aria-label', this.translate(`timeline.${direction}`));
        button.setAttribute('aria-hidden', 'true');
        button.tabIndex = -1;
        icon.src = `/images/arrow-${direction === 'previous' ? 'previous' : 'next'}-grey.svg`;
        icon.alt = '';
        icon.loading = 'eager';
        icon.setAttribute('aria-hidden', 'true');
        button.append(icon);

        return button;
    }

    bindTimelineNavigation(navigator, scrollers, buttons, options = {}) {
        const rowSelectors = {
            year: '.calendar-timeline-year',
            month: '.calendar-timeline-month',
            day: '.calendar-timeline-day'
        };
        const scrollTimers = new Map();
        const setInteraction = (row) => {
            navigator.dataset.timelineInteraction = row;
        };
        const settleInteraction = (row) => {
            window.clearTimeout(scrollTimers.get(row));
            scrollTimers.set(row, window.setTimeout(() => {
                if (navigator.dataset.timelineInteraction === row) {
                    delete navigator.dataset.timelineInteraction;
                    this.updateActiveDateButton();
                }
            }, 140));
        };
        const updateButtons = (row) => {
            const scroller = scrollers[row];
            const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

            this.updateTimelineScrollButton(buttons[row].previous, scroller.scrollLeft > 2);
            this.updateTimelineScrollButton(buttons[row].next, scroller.scrollLeft < maxScroll - 2);
        };
        const handleScroll = (row) => {
            const scroller = scrollers[row];

            updateButtons(row);
            if (navigator.dataset.timelineInteraction !== row) {
                return;
            }

            const centered = this.getCenteredTimelineElement(scroller, rowSelectors[row]);
            const date = this.getTimelineDateFromElement(centered, row);

            if (date) {
                this.setTimelineDate(navigator, date, {
                    source: row,
                    behavior: 'smooth'
                });
            }
            settleInteraction(row);
        };
        const updateDimensions = () => {
            const visibleDayCount = window.innerWidth > 800 ? 14 : 9;
            const dayScrollerWidth = scrollers.day.clientWidth;
            const yearScrollerWidth = scrollers.year.clientWidth;
            const monthScrollerWidth = scrollers.month.clientWidth;

            if (dayScrollerWidth <= 0 || yearScrollerWidth <= 0 || monthScrollerWidth <= 0) {
                return false;
            }

            navigator.style.setProperty(
                '--calendar-timeline-day-width',
                `${dayScrollerWidth / visibleDayCount}px`
            );
            navigator.style.setProperty(
                '--calendar-timeline-year-width',
                `${Math.min(170, Math.max(96, yearScrollerWidth * 0.34))}px`
            );
            navigator.style.setProperty(
                '--calendar-timeline-month-width',
                `${Math.min(124, Math.max(72, monthScrollerWidth * 0.27))}px`
            );
            window.requestAnimationFrame(() => {
                this.syncTimelineToDate(navigator, this.selectedDate, 'auto');
                Object.keys(scrollers).forEach(updateButtons);
                options.onDayGeometryChange?.();
            });
            return true;
        };

        this.timelineDimensionUpdate = updateDimensions;

        Object.entries(scrollers).forEach(([row, scroller]) => {
            ['pointerdown', 'touchstart', 'wheel'].forEach((eventName) => {
                scroller.addEventListener(eventName, () => setInteraction(row), { passive: true });
            });
            scroller.addEventListener('scroll', () => handleScroll(row), { passive: true });
        });
        navigator.addEventListener('click', (event) => {
            const marker = event.target.closest('.calendar-timeline-marker');

            if (marker) {
                event.preventDefault();
                event.stopPropagation();
                this.openTimelineEventDetails(marker);
                return;
            }

            const period = event.target.closest('.calendar-timeline-period');

            if (!period || period.disabled) {
                return;
            }

            const row = period.classList.contains('calendar-timeline-year')
                ? 'year'
                : 'month';
            const date = this.getTimelineDateFromElement(period, row);

            if (date) {
                delete navigator.dataset.timelineInteraction;
                this.setTimelineDate(navigator, date, {
                    behavior: 'smooth',
                    commit: true
                });
            }
        });
        Object.entries(buttons).forEach(([row, rowButtons]) => {
            rowButtons.previous.addEventListener('click', () => {
                this.shiftTimeline(navigator, scrollers[row], row, -1, rowSelectors[row]);
            });
            rowButtons.next.addEventListener('click', () => {
                this.shiftTimeline(navigator, scrollers[row], row, 1, rowSelectors[row]);
            });
        });
        updateDimensions();
        window.requestAnimationFrame(updateDimensions);
        if (typeof ResizeObserver === 'function') {
            this.timelineResizeObserver = new ResizeObserver(updateDimensions);
            this.timelineResizeObserver.observe(navigator);
        }
    }

    updateTimelineScrollButton(button, isVisible) {
        button.classList.toggle('visible', isVisible);
        button.setAttribute('aria-hidden', String(!isVisible));
        button.tabIndex = isVisible ? 0 : -1;
    }

    getCenteredTimelineElement(scroller, selector) {
        const center = scroller.scrollLeft + (scroller.clientWidth / 2);
        let closest = null;
        let closestDistance = Infinity;

        scroller.querySelectorAll(selector).forEach((element) => {
            const elementCenter = element.offsetLeft + (element.offsetWidth / 2);
            const distance = Math.abs(elementCenter - center);

            if (distance < closestDistance) {
                closest = element;
                closestDistance = distance;
            }
        });

        return closest;
    }

    getTimelineDateFromElement(element, row) {
        if (!element) {
            return null;
        }

        if (row === 'day') {
            return this.parseDate(element.dataset.date);
        }

        const currentDate = new Date(this.selectedDate);
        const targetYear = Number(element.dataset.year || currentDate.getFullYear());
        const targetMonth = row === 'month'
            ? Number(element.dataset.monthIndex)
            : currentDate.getMonth();
        const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();

        return new Date(targetYear, targetMonth, Math.min(currentDate.getDate(), lastDay));
    }

    setTimelineDate(navigator, date, options = {}) {
        const nextDate = this.clampTimelineDate(date);
        const nextKey = this.formatDateKey(nextDate);

        if (nextKey !== this.formatDateKey(this.selectedDate)) {
            this.selectedDate = nextDate;
            this.focusedYear = nextDate.getFullYear();
        }
        this.updateTimelineActiveStates(navigator, nextDate);
        this.syncTimelineToDate(navigator, nextDate, options.behavior || 'smooth', options.source);
        if (options.commit) {
            this.updateActiveDateButton();
        }
    }

    clampTimelineDate(date) {
        const days = this.getDateNavigatorDays();
        const firstDay = days[0];
        const lastDay = days[days.length - 1];
        const nextDate = new Date(date);

        if (firstDay && nextDate < firstDay) {
            return new Date(firstDay);
        }
        if (lastDay && nextDate > lastDay) {
            return new Date(lastDay);
        }

        return nextDate;
    }

    updateTimelineActiveStates(navigator, date) {
        const dateKey = this.formatDateKey(date);
        const yearKey = String(date.getFullYear());
        const monthKey = dateKey.slice(0, 7);

        navigator.querySelectorAll('.calendar-timeline-year').forEach((period) => {
            period.classList.toggle('active', period.dataset.year === yearKey);
        });
        navigator.querySelectorAll('.calendar-timeline-month').forEach((period) => {
            period.classList.toggle('active', period.dataset.month === monthKey);
        });
        navigator.querySelectorAll('.calendar-timeline-day').forEach((day) => {
            day.classList.toggle('active', day.dataset.date === dateKey);
        });
    }

    syncTimelineToDate(navigator, date, behavior = 'smooth', source = null) {
        const dateKey = this.formatDateKey(date);
        const targets = {
            year: navigator.querySelector(`.calendar-timeline-year[data-year="${date.getFullYear()}"]`),
            month: navigator.querySelector(`.calendar-timeline-month[data-month="${dateKey.slice(0, 7)}"]`),
            day: navigator.querySelector(`.calendar-timeline-day[data-date="${dateKey}"]`)
        };

        Object.entries(targets).forEach(([row, target]) => {
            if (!target || row === source) {
                return;
            }
            const scroller = navigator.querySelector(`.calendar-timeline-scroller-${row}s`);

            if (scroller) {
                this.centerTimelineElement(scroller, target, behavior);
            }
        });
    }

    shiftTimeline(navigator, scroller, row, direction, selector) {
        const elements = [...scroller.querySelectorAll(selector)];
        const centered = this.getCenteredTimelineElement(scroller, selector);
        const currentIndex = Math.max(0, elements.indexOf(centered));
        const step = row === 'day' ? 7 : 1;
        const target = elements[Math.max(0, Math.min(elements.length - 1, currentIndex + (direction * step)))];
        const date = this.getTimelineDateFromElement(target, row);

        if (date) {
            this.setTimelineDate(navigator, date, {
                behavior: 'smooth',
                commit: true
            });
        }
    }

    createTimelinePeriod(type, label, date) {
        const period = document.createElement('button');
        const periodLabel = document.createElement('span');
        const dateKey = this.formatDateKey(date);

        period.type = 'button';
        period.className = `calendar-timeline-period calendar-timeline-${type}`;
        period.dataset.date = dateKey;
        period.dataset.year = String(date.getFullYear());
        if (type === 'month') {
            period.dataset.month = dateKey.slice(0, 7);
            period.dataset.monthIndex = String(date.getMonth());
        }
        periodLabel.className = 'calendar-timeline-period-label';
        periodLabel.textContent = label;
        period.append(periodLabel);
        period.setAttribute('aria-label', type === 'year'
            ? this.formatDateYear(date)
            : new Intl.DateTimeFormat(this.locale, { month: 'long', year: 'numeric' }).format(date));

        return period;
    }

    createTimelineDay(date, events, dayHeight) {
        const day = document.createElement('div');
        const label = document.createElement('span');
        const weekday = document.createElement('span');
        const dayNumber = document.createElement('span');
        const dateKey = this.formatDateKey(date);

        day.className = 'calendar-timeline-day';
        day.dataset.date = dateKey;
        day.style.height = `${dayHeight}px`;
        day.style.minHeight = `${dayHeight}px`;
        day.setAttribute('aria-label', `${this.formatFullDate(date)} · ${events.length}`);
        if (dateKey === this.formatDateKey(this.selectedDate)) {
            day.classList.add('active');
        }
        if (dateKey === this.formatDateKey(this.getToday())) {
            day.classList.add('today');
        }
        if (date.getDay() === 0) {
            day.classList.add('weekend');
        }
        if (date.getDate() === 1) {
            day.classList.add('month-start');
        }
        if (date.getMonth() === 0 && date.getDate() === 1) {
            day.classList.add('year-start');
        }

        label.className = 'calendar-timeline-day-label';
        weekday.className = 'calendar-timeline-day-weekday';
        weekday.textContent = this.formatTimelineWeekday(date);
        dayNumber.className = 'calendar-timeline-day-number';
        dayNumber.textContent = this.formatDateDay(date);
        label.append(weekday, dayNumber);

        day.append(label);

        return day;
    }

    createTimelineEventMarkers(events, days, lanes, markersHeight, markerHeight, markerGap) {
        const markers = document.createElement('span');
        const dayIndexes = new Map(days.map((date, index) => [
            this.formatDateKey(date),
            index
        ]));
        const laneCount = Math.max(lanes.size > 0 ? Math.max(...lanes.values()) + 1 : 0, 1);
        const entries = events.map((event) => {
            const lane = lanes.get(this.getEventId(event));
            const startIndex = dayIndexes.get(event.start_date);
            const endIndex = dayIndexes.get(event.end_date || event.start_date);

            if (!Number.isInteger(lane) || !Number.isInteger(startIndex) || !Number.isInteger(endIndex)) {
                return null;
            }

            return {
                event,
                lane,
                startIndex,
                endIndex
            };
        }).filter(Boolean);

        markers.className = 'calendar-timeline-markers';
        markers.style.height = `${markersHeight + 2}px`;
        markers.style.gridTemplateColumns = `repeat(${days.length}, var(--calendar-timeline-day-width))`;
        markers.style.gridTemplateRows = `repeat(${laneCount}, ${markerHeight}px)`;
        markers.style.rowGap = `${markerGap}px`;
        this.timelineMarkerStates.set(markers, {
            entries,
            frame: null,
            maxDayIndex: Math.max(0, days.length - 1),
            renderedStart: null,
            renderedEnd: null,
            scroller: null
        });

        return markers;
    }

    bindTimelineEventVirtualization(scroller, markers) {
        const state = this.timelineMarkerStates.get(markers);

        this.timelineMarkerCleanup?.();
        if (!state) {
            return () => false;
        }

        const update = () => {
            if (state.frame) {
                return false;
            }

            state.frame = window.requestAnimationFrame(() => {
                state.frame = null;
                if (!markers.isConnected || !scroller.isConnected) {
                    return;
                }

                const firstDay = scroller.querySelector('.calendar-timeline-day');
                const dayWidth = firstDay?.offsetWidth || 1;
                const visibleStart = Math.floor(scroller.scrollLeft / dayWidth);
                const visibleEnd = Math.ceil((scroller.scrollLeft + scroller.clientWidth) / dayWidth) - 1;
                const start = Math.max(0, visibleStart - TIMELINE_MARKER_OVERSCAN_DAYS);
                const end = Math.min(
                    state.maxDayIndex,
                    visibleEnd + TIMELINE_MARKER_OVERSCAN_DAYS
                );

                this.renderTimelineEventMarkerWindow(markers, state, start, end);
            });

            return true;
        };
        const cleanup = () => {
            scroller.removeEventListener('scroll', update);
            if (state.frame) {
                window.cancelAnimationFrame(state.frame);
                state.frame = null;
            }
            if (this.timelineMarkerCleanup === cleanup) {
                this.timelineMarkerCleanup = null;
            }
        };

        state.scroller = scroller;
        scroller.addEventListener('scroll', update, { passive: true });
        this.timelineMarkerCleanup = cleanup;
        window.requestAnimationFrame(update);

        return update;
    }

    renderTimelineEventMarkerWindow(markers, state, start, end) {
        if (state.renderedStart === start && state.renderedEnd === end) {
            return false;
        }

        const fragment = document.createDocumentFragment();
        const tooltip = markers.closest('.calendar-timeline-shell')
            ?.querySelector('.calendar-timeline-event-tooltip');

        state.entries.forEach((entry) => {
            if (entry.endIndex >= start && entry.startIndex <= end) {
                fragment.append(this.createTimelineEventMarker(entry));
            }
        });
        if (tooltip) {
            tooltip.hidden = true;
            delete tooltip.dataset.eventId;
        }
        markers.replaceChildren(fragment);
        state.renderedStart = start;
        state.renderedEnd = end;

        return true;
    }

    createTimelineEventMarker({ event, lane, startIndex, endIndex }) {
        const marker = document.createElement('button');
        const markerTitle = document.createElement('span');
        const eventTitle = this.getLocalizedValue(event.name);

        marker.type = 'button';
        marker.className = 'calendar-timeline-marker';
        marker.dataset.eventId = this.getEventId(event);
        marker.dataset.eventUid = event.uid || '';
        marker.dataset.eventTitle = eventTitle;
        marker.dataset.eventDiscipline = this.getEventDisciplines(event)
            .map((discipline) => this.disciplineLabels[discipline] || discipline)
            .join(' · ');
        marker.dataset.eventColor = this.getTimelineEventColor(event);
        marker.style.gridColumn = `${startIndex + 1} / span ${Math.max(1, endIndex - startIndex + 1)}`;
        marker.style.gridRow = String(lane + 1);
        marker.style.background = marker.dataset.eventColor;
        marker.setAttribute(
            'aria-label',
            [eventTitle, marker.dataset.eventDiscipline].filter(Boolean).join(' · ')
        );
        markerTitle.className = 'calendar-timeline-marker-title';
        markerTitle.textContent = eventTitle;
        marker.append(markerTitle);

        return marker;
    }

    openTimelineEventDetails(marker) {
        const eventId = marker?.dataset.eventId || '';
        const event = this.events.find((item) => this.getEventId(item) === eventId);
        const identifier = event?.uid || marker?.dataset.eventUid || '';

        if (!event || !identifier) {
            return false;
        }

        return this.openEventModal(null, event, {
            identifier,
            returnFocus: marker
        });
    }

    getTimelineEventLayout(events) {
        const laneEndDates = [];
        const lanes = new Map();

        [...events].sort((eventA, eventB) => (
            this.compareEventValue(eventA.start_date, eventB.start_date)
            || this.getTimelineEventDuration(eventB) - this.getTimelineEventDuration(eventA)
            || this.compareEventValue(eventB.end_date || eventB.start_date, eventA.end_date || eventA.start_date)
            || this.compareEventValue(this.getEventId(eventA), this.getEventId(eventB))
        )).forEach((event) => {
            const startDate = event.start_date;
            const endDate = event.end_date || startDate;
            let lane = laneEndDates.findIndex((laneEndDate) => laneEndDate < startDate);

            if (lane < 0) {
                lane = laneEndDates.length;
            }
            laneEndDates[lane] = endDate;
            lanes.set(this.getEventId(event), lane);
        });

        return {
            lanes,
            laneCount: laneEndDates.length
        };
    }

    getTimelineEventDuration(event) {
        const startDate = this.parseDate(event.start_date);
        const endDate = this.parseDate(event.end_date || event.start_date);

        if (!startDate || !endDate) {
            return 0;
        }

        return Math.max(1, Math.round((endDate - startDate) / (24 * 60 * 60 * 1000)) + 1);
    }

    getTimelineEventDateKeys(event) {
        const startDate = this.parseDate(event.start_date);
        const endDate = this.parseDate(event.end_date || event.start_date);
        const dateKeys = [];

        if (!startDate || !endDate) {
            return dateKeys;
        }

        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            dateKeys.push(this.formatDateKey(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return dateKeys;
    }

    getTimelineEventColor(event) {
        const discipline = this.getEventDiscipline(event);

        return this.getDisciplineTagColor(discipline) || 'var(--color-red)';
    }

    createLegacyDateNavigator() {
        const navigator = document.createElement('div');
        const scroller = document.createElement('div');
        const track = document.createElement('div');
        const frame = document.createElement('div');
        const days = this.getDateNavigatorDays();
        const filteredEvents = this.getFilteredEvents();

        navigator.className = 'calendar-date-navigator';
        scroller.className = 'calendar-date-scroller';
        track.className = 'calendar-date-track';
        frame.className = 'calendar-date-selection-frame';
        frame.setAttribute('aria-hidden', 'true');

        days.forEach((date) => {
            const eventsCount = this.getEventsForDateFromList(date, filteredEvents).length;

            track.append(this.createDateButton(date, eventsCount));
        });

        scroller.append(track);
        scroller.addEventListener('scroll', () => {
            this.queueDateScaleUpdate(scroller);
            window.clearTimeout(this.dateScrollTimer);
            this.dateScrollTimer = window.setTimeout(() => {
                const dateKey = this.getCenteredDateKey(scroller);

                if (dateKey && dateKey !== this.formatDateKey(this.selectedDate)) {
                    this.selectDateByKey(dateKey, { scroll: false });
                }
            }, 90);
        });

        navigator.append(frame, scroller);

        return navigator;
    }

    createDateButton(date, eventsCount = 0) {
        const button = document.createElement('button');
        const prefix = document.createElement('span');
        const weekday = document.createElement('span');
        const day = document.createElement('span');
        const month = document.createElement('span');
        const year = document.createElement('span');
        const count = document.createElement('span');
        const dateKey = this.formatDateKey(date);

        button.type = 'button';
        button.className = 'calendar-date-button';
        button.dataset.date = dateKey;
        button.style.setProperty('--date-scale', '0.82');
        button.setAttribute('aria-label', this.formatFullDate(date));
        if (dateKey === this.formatDateKey(this.selectedDate)) {
            button.classList.add('active');
        }
        if (this.isWeekend(date)) {
            button.classList.add('weekend');
        }

        prefix.className = 'calendar-date-prefix';
        weekday.className = 'calendar-date-weekday';
        day.className = 'calendar-date-day';
        month.className = 'calendar-date-month';
        year.className = 'calendar-date-year';
        count.className = 'calendar-date-count';
        prefix.textContent = 'à partir de';
        weekday.textContent = this.formatWeekday(date);
        day.textContent = this.formatDateDay(date);
        month.textContent = this.formatDateMonth(date);
        year.textContent = this.formatDateYear(date);
        count.textContent = eventsCount > 0 ? String(eventsCount) : '';

        button.append(prefix, weekday, day, month, year, count);
        button.addEventListener('click', () => this.selectDate(date, { scroll: true }));

        return button;
    }

    createAgendaList(options = {}) {
        const list = document.createElement('div');
        const occurrences = this.getAgendaOccurrences();

        list.className = 'calendar-agenda';

        if (occurrences.length === 0) {
            const empty = document.createElement('div');

            empty.className = 'calendar-empty';
            empty.textContent = this.getEmptyEventsMessage();
            list.append(empty);

            return list;
        }

        const items = occurrences.flatMap(({ date, events }) => events.map((event) => ({
            date,
            dateKey: this.formatDateKey(date),
            event
        })));
        const state = this.createAgendaRenderState(items, options);

        list.append(state.topSentinel, state.bottomSentinel);
        this.agendaRenderStates.set(list, state);
        this.renderAgendaItemRange(list, state, state.start, state.end, 'append');
        this.markAgendaTodayAnchor(list);
        this.updateAgendaLoadSentinels(state);

        return list;
    }

    createUpcomingPreview(limit = 5) {
        const list = document.createElement('div');
        const todayKey = this.formatDateKey(this.getToday());
        const events = this.getFilteredEvents()
            .filter((event) => String(event?.start_date || '') >= todayKey)
            .sort((eventA, eventB) => (
                this.compareEventValue(eventA.start_date, eventB.start_date)
                || this.compareEventValue(
                    this.getLocalizedValue(eventA.name),
                    this.getLocalizedValue(eventB.name)
                )
            ))
            .slice(0, Math.max(0, limit));

        list.className = 'home-upcoming-list';

        if (events.length === 0) {
            const empty = document.createElement('div');

            empty.className = 'home-upcoming-empty';
            empty.textContent = this.getEmptyEventsMessage();
            list.append(empty);
            return list;
        }

        events.forEach((event) => {
            list.append(this.createUpcomingPreviewCard(event));
        });

        return list;
    }

    createUpcomingPreviewCard(event) {
        const card = document.createElement('a');
        const content = document.createElement('span');
        const date = document.createElement('span');
        const name = document.createElement('span');
        const location = document.createElement('span');
        const tags = document.createElement('span');
        const arrow = document.createElement('img');
        const imageUrl = this.getEventMiniImageUrl(event);
        const startDate = this.parseDate(event.start_date);

        card.href = this.getEventPublicPath(event);
        card.className = 'home-upcoming-card';
        card.setAttribute(
            'aria-label',
            `${this.translate('eventModal.dialog')} : ${this.getLocalizedValue(event?.name)}`
        );
        content.className = 'home-upcoming-card-content';
        date.className = 'home-upcoming-card-date';
        date.textContent = startDate ? this.formatAgendaDayTitle(startDate) : '';
        name.className = 'home-upcoming-card-title';
        name.textContent = this.getLocalizedValue(event.name);
        location.className = 'home-upcoming-card-location';
        location.textContent = this.formatEventLocationPlace(event);
        tags.className = 'home-upcoming-card-tags';
        this.getDisplayTags(event.tags || [], event).slice(0, 3).forEach(({ label, color }) => {
            const tag = document.createElement('span');

            tag.className = 'home-upcoming-card-tag';
            tag.textContent = label;
            if (color) {
                tag.style.setProperty('--home-upcoming-tag-color', color);
            }
            tags.append(tag);
        });
        arrow.className = 'home-upcoming-card-arrow';
        arrow.src = '/images/arrow-next-grey-bold.svg';
        arrow.alt = '';
        arrow.setAttribute('aria-hidden', 'true');

        if (imageUrl) {
            const image = document.createElement('img');

            image.className = 'home-upcoming-card-image';
            image.src = imageUrl;
            image.alt = '';
            image.width = 336;
            image.height = 336;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.setAttribute('aria-hidden', 'true');
            this.bindEventImageFallback(image, event, 'mini');
            image.addEventListener('error', () => {
                window.requestAnimationFrame(() => {
                    if (image.isConnected && image.complete && image.naturalWidth === 0) {
                        image.remove();
                        card.classList.add('home-upcoming-card-without-image');
                    }
                });
            });
            card.append(image);
        } else {
            card.classList.add('home-upcoming-card-without-image');
        }

        content.append(date, name);
        if (location.textContent) content.append(location);
        if (tags.childElementCount) content.append(tags);
        card.append(content, arrow);
        card.addEventListener('click', (clickEvent) => {
            clickEvent.preventDefault();
            this.openEventModal(card, event, { returnFocus: card });
        });

        return card;
    }

    createAgendaRenderState(items, options) {
        const initialCount = Math.min(AGENDA_BATCH_SIZE, items.length);

        return {
            items,
            options,
            start: 0,
            end: initialCount,
            sections: new Map(),
            topSentinel: this.createAgendaLoadSentinel('before'),
            bottomSentinel: this.createAgendaLoadSentinel('after'),
            observer: null,
            frame: null,
            root: null,
            loading: false
        };
    }

    createAgendaLoadSentinel(direction) {
        const sentinel = document.createElement('div');

        sentinel.className = `calendar-agenda-load-sentinel calendar-agenda-load-sentinel-${direction}`;
        sentinel.dataset.agendaLoadDirection = direction;
        sentinel.setAttribute('aria-hidden', 'true');

        return sentinel;
    }

    groupAgendaItems(items) {
        return items.reduce((groups, item) => {
            const latestGroup = groups.at(-1);

            if (latestGroup?.dateKey === item.dateKey) {
                latestGroup.events.push(item.event);
            } else {
                groups.push({
                    date: item.date,
                    dateKey: item.dateKey,
                    events: [item.event]
                });
            }

            return groups;
        }, []);
    }

    renderAgendaItemRange(list, state, start, end, direction) {
        const groups = this.groupAgendaItems(state.items.slice(start, end));

        if (direction === 'before') {
            let insertionPoint = list.querySelector('.calendar-agenda-day') || state.bottomSentinel;

            [...groups].reverse().forEach((group) => {
                const existingSection = state.sections.get(group.dateKey);

                if (existingSection) {
                    const cards = document.createDocumentFragment();
                    const firstCard = existingSection.querySelector('.calendar-agenda-event-wrapper');

                    group.events.forEach((event) => {
                        cards.append(this.createAgendaEventCard(event, group.date, state.options));
                    });
                    existingSection.insertBefore(cards, firstCard);
                    insertionPoint = existingSection;
                    return;
                }

                const section = this.createAgendaDaySection(group.date, group.events, state.options);

                list.insertBefore(section, insertionPoint);
                state.sections.set(group.dateKey, section);
                insertionPoint = section;
            });
            return;
        }

        groups.forEach((group) => {
            const existingSection = state.sections.get(group.dateKey);

            if (existingSection) {
                group.events.forEach((event) => {
                    existingSection.append(this.createAgendaEventCard(event, group.date, state.options));
                });
                return;
            }

            const section = this.createAgendaDaySection(group.date, group.events, state.options);

            list.insertBefore(section, state.bottomSentinel);
            state.sections.set(group.dateKey, section);
        });
    }

    markAgendaTodayAnchor(list) {
        const todayKey = this.formatDateKey(this.getToday());
        const sections = [...list.querySelectorAll('.calendar-agenda-day[data-date]')];
        const target = sections.find((section) => section.dataset.date >= todayKey)
            || sections.at(-1);

        target?.classList.add('calendar-agenda-today-anchor');
    }

    updateAgendaLoadSentinels(state) {
        state.topSentinel.hidden = state.start <= 0;
        state.bottomSentinel.hidden = state.end >= state.items.length;
    }

    initializeAgendaProgressiveLoading(container, list) {
        const state = this.agendaRenderStates.get(list);

        if (!state) {
            return false;
        }

        this.cleanupAgendaProgressiveLoading(this.activeAgendaList);
        this.activeAgendaList = list;
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (!list.isConnected || this.activeAgendaList !== list) {
                    return;
                }

                const modalScroller = container.closest('.app-modal__scroll');
                const usesViewportScroll = container.matches('.home-events-view-list');

                state.root = modalScroller || (usesViewportScroll ? null : list);
                if (typeof IntersectionObserver !== 'function') {
                    this.renderAgendaItemRange(list, state, 0, state.start, 'before');
                    this.renderAgendaItemRange(list, state, state.end, state.items.length, 'after');
                    state.start = 0;
                    state.end = state.items.length;
                    this.updateAgendaLoadSentinels(state);
                    return;
                }

                state.observer = new IntersectionObserver((entries) => {
                    const visibleEntry = entries.find((entry) => entry.isIntersecting && !entry.target.hidden);

                    if (visibleEntry) {
                        this.queueAgendaBatch(list, state, visibleEntry.target.dataset.agendaLoadDirection);
                    }
                }, {
                    root: state.root,
                    rootMargin: '600px 0px'
                });
                state.observer.observe(state.topSentinel);
                state.observer.observe(state.bottomSentinel);
            });
        });

        return true;
    }

    queueAgendaBatch(list, state, direction) {
        if (state.loading || !list.isConnected) {
            return false;
        }

        state.loading = true;
        state.frame = window.requestAnimationFrame(() => {
            this.renderAgendaBatch(list, state, direction);
            state.loading = false;
            state.frame = null;
        });

        return true;
    }

    renderAgendaBatch(list, state, direction) {
        if (direction === 'before' && state.start > 0) {
            const nextStart = Math.max(0, state.start - AGENDA_BATCH_SIZE);
            const previousHeight = state.root.scrollHeight;
            const previousScrollTop = state.root.scrollTop;

            this.renderAgendaItemRange(list, state, nextStart, state.start, 'before');
            state.start = nextStart;
            this.updateAgendaLoadSentinels(state);
            state.root.scrollTop = previousScrollTop + (state.root.scrollHeight - previousHeight);
            return true;
        }
        if (direction === 'after' && state.end < state.items.length) {
            const nextEnd = Math.min(state.items.length, state.end + AGENDA_BATCH_SIZE);

            this.renderAgendaItemRange(list, state, state.end, nextEnd, 'after');
            state.end = nextEnd;
            this.updateAgendaLoadSentinels(state);
            return true;
        }

        this.updateAgendaLoadSentinels(state);
        return false;
    }

    cleanupAgendaProgressiveLoading(list = null) {
        const targetList = list || this.activeAgendaList;
        const state = targetList ? this.agendaRenderStates.get(targetList) : null;

        state?.observer?.disconnect();
        if (state?.frame) {
            window.cancelAnimationFrame(state.frame);
        }
        if (targetList === this.activeAgendaList) {
            this.activeAgendaList = null;
        }

        return Boolean(state);
    }

    getEmptyEventsMessage() {
        const messages = [this.translate('filters.noResults')];

        if (this.getActiveFilterCount() > 0) {
            messages.push(this.translate('filters.reviewFilters'));
        }

        return messages.join(' ');
    }

    createAgendaDaySection(date, events, options = {}) {
        const section = document.createElement('section');
        const heading = document.createElement('h2');

        section.className = 'calendar-agenda-day';
        section.dataset.date = this.formatDateKey(date);
        if (section.dataset.date === this.formatDateKey(this.getToday())) {
            section.classList.add('calendar-agenda-today');
        }
        heading.className = 'calendar-agenda-day-title';
        heading.textContent = this.formatAgendaDayTitle(date);
        section.append(heading);

        events.forEach((event) => {
            section.append(this.createAgendaEventCard(event, date, options));
        });

        return section;
    }

    createAgendaEventCard(event, date, options = {}) {
        const wrapper = document.createElement('div');
        const mask = document.createElement('div');
        const card = document.createElement('article');
        const layout = document.createElement('div');
        const body = document.createElement('div');
        const textContent = document.createElement('div');
        const name = document.createElement('h3');
        const summary = document.createElement('p');
        const location = document.createElement('div');
        const details = document.createElement('div');
        const navigationArrow = options.showNavigationArrow
            ? this.createAgendaEventNavigationArrow(event)
            : null;
        const key = this.getEventDetailKey(event, date);
        const tornMaskOffset = `${this.getTornMaskOffset(key)}px`;
        const description = this.getLocalizedValue(event.description);
        const meta = [this.formatEventDateRange(event), event.address].filter(Boolean).join(' · ');

        wrapper.className = 'calendar-agenda-event-wrapper';
        mask.className = 'calendar-agenda-event-mask';
        card.className = 'calendar-agenda-event';
        card.dataset.eventKey = key;
        wrapper.style.setProperty('--calendar-torn-mask-y', tornMaskOffset);
        mask.style.setProperty('--calendar-torn-mask-y', tornMaskOffset);
        mask.style.maskPosition = `center ${tornMaskOffset}`;
        mask.style.webkitMaskPosition = `center ${tornMaskOffset}`;
        card.style.setProperty('--calendar-torn-mask-y', tornMaskOffset);
        if (this.expandedEventKeys.has(key)) {
            card.classList.add('open');
        }

        layout.className = 'calendar-agenda-event-layout';
        if (navigationArrow) {
            layout.classList.add('calendar-agenda-event-layout-with-navigation');
        }
        body.className = 'calendar-agenda-event-body';
        textContent.className = 'calendar-agenda-event-text';
        name.className = 'calendar-agenda-event-name';
        summary.className = 'calendar-agenda-event-summary';
        location.className = 'calendar-agenda-event-location';
        details.className = 'calendar-agenda-event-details';

        const nameLink = document.createElement('a');

        nameLink.href = this.getEventPublicPath(event);
        nameLink.textContent = this.getLocalizedValue(event.name);
        nameLink.addEventListener('click', (clickEvent) => clickEvent.preventDefault());
        name.append(nameLink);
        summary.textContent = description;
        this.appendEventLocation(location, event, options);

        textContent.append(name);
        if (location.textContent) {
            textContent.append(location);
        }
        if (summary.textContent) {
            textContent.append(summary);
        }
        if (meta) {
            details.textContent = meta;
            textContent.append(details);
        }
        const tags = this.createAgendaEventTags(event, options);

        body.append(textContent);
        if (navigationArrow) {
            body.classList.add('calendar-agenda-event-body-with-navigation');
            body.append(navigationArrow);
        }
        body.append(tags);

        const thumbnail = this.createAgendaEventThumbnail(event);

        if (thumbnail) {
            layout.append(thumbnail);
        } else {
            layout.classList.add('calendar-agenda-event-layout-without-thumbnail');
        }
        layout.append(body);
        if (navigationArrow) {
            navigationArrow.addEventListener('click', (clickEvent) => {
                clickEvent.stopPropagation();
                this.openEventModal(card, event, {
                    returnFocus: navigationArrow
                });
            });
        }
        card.append(layout);
        card.addEventListener('click', () => this.openEventModal(card, event));

        mask.append(card);
        wrapper.append(mask);

        return wrapper;
    }

    createAgendaEventNavigationArrow(event) {
        const arrow = document.createElement('button');
        const icon = document.createElement('img');

        arrow.type = 'button';
        arrow.className = 'calendar-agenda-event-arrow';
        arrow.setAttribute(
            'aria-label',
            `${this.translate('eventModal.dialog')} : ${this.getLocalizedValue(event?.name)}`
        );
        icon.src = '/images/arrow-next-grey-bold.svg';
        icon.alt = '';
        icon.setAttribute('aria-hidden', 'true');
        arrow.append(icon);

        return arrow;
    }

    createAgendaEventThumbnail(event) {
        const imageUrl = this.getEventMiniImageUrl(event);

        if (!imageUrl) {
            return null;
        }

        const thumbnail = document.createElement('figure');
        const image = document.createElement('img');

        thumbnail.className = 'calendar-agenda-event-thumbnail';
        thumbnail.setAttribute('aria-hidden', 'true');
        image.src = imageUrl;
        image.alt = '';
        image.width = 336;
        image.height = 336;
        image.loading = 'lazy';
        image.decoding = 'async';
        this.bindEventImageFallback(image, event, 'mini');
        thumbnail.append(image);

        return thumbnail;
    }

    createAgendaEventTags(event, options = {}) {
        const tags = document.createElement('div');
        const groups = {
            discipline: document.createElement('div'),
            additional: document.createElement('div')
        };

        tags.className = 'calendar-agenda-event-tags';
        tags.classList.toggle('calendar-agenda-event-tags-merged', Boolean(options.mergeTagGroups));
        groups.discipline.className = 'calendar-agenda-event-tag-group calendar-agenda-event-tag-group-disciplines';
        groups.additional.className = 'calendar-agenda-event-tag-group calendar-agenda-event-tag-group-additional';

        this.getDisplayTags(event.tags || [], event).forEach(({ id, label, color, type }) => {
            const tag = document.createElement('span');

            tag.className = 'calendar-agenda-event-tag';
            tag.classList.add(`calendar-agenda-event-tag-${type}`);
            tag.dataset.tag = id;
            tag.textContent = label;
            if (color) {
                tag.style.setProperty('--calendar-agenda-event-tag-color', color);
            }
            if (options.mergeTagGroups) {
                tags.append(tag);
            } else {
                groups[type]?.append(tag);
            }
        });

        if (!options.mergeTagGroups) {
            Object.values(groups).forEach((group) => {
                if (group.childElementCount > 0) {
                    tags.append(group);
                }
            });
        }

        return tags;
    }

    async openEventDetailsByUid(uid, options = {}) {
        const eventUid = String(uid || '');
        const event = this.events.find((item) => String(item?.uid || '') === eventUid)
            || { uid: eventUid };

        if (!eventUid || this.activeEventModal) {
            return false;
        }

        this.validationEventUid = eventUid;
        const eventKeyPrefix = `${eventUid}-`;
        const listContainer = this.viewContainers.get('list') || this.container;
        const card = [...(listContainer?.querySelectorAll('.calendar-agenda-event[data-event-key]') || [])]
            .find((item) => String(item.dataset.eventKey || '').startsWith(eventKeyPrefix));

        return this.openEventModal(card || null, event, {
            identifier: eventUid,
            preserveRoute: Boolean(options.preserveRoute)
        });
    }

    async openEventDetailsByCanonicalName(canonicalName, options = {}) {
        const identifier = String(canonicalName || '').trim();

        if (!identifier || this.activeEventModal) {
            return false;
        }

        return this.openEventModal(
            null,
            { canonical_name: { [this.locale]: identifier } },
            {
                identifier,
                preserveRoute: Boolean(options.preserveRoute)
            }
        );
    }

    openEventModal(card, event, {
        identifier = event?.uid,
        temporarySource = null,
        preserveRoute = false,
        returnFocus = null
    } = {}) {
        const eventIdentifier = String(identifier || '').trim();

        if (this.activeEventModal || !eventIdentifier || !this.app?.simpleModal) {
            temporarySource?.remove();
            return false;
        }

        this.pendingEventDetails = {
            card,
            event,
            identifier: eventIdentifier,
            temporarySource
        };
        this.app.simpleModal.contents.eventDetails = this.getContents().eventDetails;
        const opened = this.app.simpleModal.open('eventDetails', returnFocus || card);

        if (!opened) {
            this.cleanupEventDetails();
            return false;
        }

        this.activeEventModal = this.app.simpleModal.modalStack.at(-1) || null;
        if (!preserveRoute) {
            this.app?.home?.navigateToEvent?.(event);
        }

        return Boolean(this.activeEventModal);
    }

    renderEventDetails(context) {
        const {
            body,
            modal,
            close
        } = context;
        const eventIdentifier = this.pendingEventDetails?.identifier;

        if (!eventIdentifier) {
            close();
            return false;
        }

        modal.classList.add('home-event-details-modal');
        body.classList.add('event-details-modal-body');
        body.replaceChildren();
        this.pendingEventDetails.context = context;

        return this.loadEventDetails(eventIdentifier, context);
    }

    reloadActiveEventDetails(identifier = this.pendingEventDetails?.event?.uid) {
        const eventIdentifier = String(identifier || '').trim();
        const context = this.pendingEventDetails?.context;

        if (
            !eventIdentifier
            || !context?.modal?.isConnected
            || !this.activeEventModal
            || !this.activeEventModal.modal.classList.contains('is-open')
        ) {
            return Promise.resolve(false);
        }

        this.pendingEventDetails.identifier = eventIdentifier;
        return this.loadEventDetails(eventIdentifier, context);
    }

    async loadEventDetails(eventIdentifier, context) {
        let event = null;
        const succeeded = await this.api.query(
            'get',
            `/events/info/${encodeURIComponent(eventIdentifier)}`,
            { locale: this.locale },
            () => {
                context.showLoading();
            },
            (response) => {
                event = response?.result || null;
                if (
                    event
                    && context.modal.isConnected
                    && this.pendingEventDetails?.identifier === eventIdentifier
                ) {
                    this.pendingEventDetails.event = event;
                    this.renderEventDetailsContent(context, event);
                    this.app?.home?.navigateToEvent?.(event, true);
                }
                context.hideLoading();
            },
            null,
            {
                onError: () => {
                    context.hideLoading();
                    if (context.modal.isConnected) {
                        context.close();
                    }
                }
            }
        );

        if (!succeeded || !event) {
            return false;
        }

        return true;
    }

    renderEventDetailsContent(context, event) {
        const {
            body,
            setTitle
        } = context;
        const details = document.createElement('article');
        const meta = this.createEventModalMeta(event);
        const actions = this.createEventModalActions(event, context);
        const image = this.createAgendaEventThumbnail(event);
        const content = document.createElement('div');
        const title = document.createElement('h2');
        const uid = this.isCurrentUserMaster()
            ? document.createElement('span')
            : null;
        const eventTitle = this.getLocalizedValue(event.name);

        setTitle(eventTitle);
        details.className = 'event-details';
        content.className = 'event-details-content';
        title.className = 'event-details-title';
        title.textContent = eventTitle;
        if (uid) {
            uid.className = 'event-details-uid';
            uid.textContent = event.uid || '';
        }
        if (image) {
            this.prepareEventModalImage(image, event);
            details.append(image);
        }
        details.append(title);
        content.append(meta, actions);
        details.append(content);
        body.replaceChildren(...(uid ? [uid, details] : [details]));

        return true;
    }

    cleanupEventDetails() {
        this.pendingEventDetails?.temporarySource?.remove();
        this.pendingEventDetails = null;
        this.activeEventModal = null;
    }

    openOrganisationDetails(organisation, returnFocus = null) {
        const uid = String(organisation?.uid || organisation?.uid_organismes || '').trim();

        if (!uid || !this.app?.simpleModal || this.activeOrganisationModal) {
            return false;
        }

        this.pendingOrganisationDetails = { uid, organisation };
        this.app.simpleModal.contents.organisationDetails = this.getContents().organisationDetails;
        const opened = this.app.simpleModal.open('organisationDetails', returnFocus);

        if (!opened) {
            this.cleanupOrganisationDetails();
            return false;
        }

        this.activeOrganisationModal = this.app.simpleModal.modalStack.at(-1) || null;
        return Boolean(this.activeOrganisationModal);
    }

    renderOrganisationDetails(context) {
        const { body, modal, close } = context;
        const uid = this.pendingOrganisationDetails?.uid;

        if (!uid) {
            close();
            return false;
        }

        modal.classList.add('home-event-details-modal', 'home-organisation-details-modal');
        body.classList.add('event-details-modal-body', 'organisation-details-modal-body');
        body.replaceChildren();
        this.pendingOrganisationDetails.context = context;

        return this.loadOrganisationDetails(uid, context);
    }

    async loadOrganisationDetails(uid, context) {
        let organisation = null;
        const succeeded = await this.api.query(
            'get',
            '/organismes/info',
            { uid },
            () => context.showLoading(),
            (response) => {
                organisation = response?.result || null;
                if (
                    organisation
                    && context.modal.isConnected
                    && this.pendingOrganisationDetails?.uid === uid
                ) {
                    this.pendingOrganisationDetails.organisation = organisation;
                    this.renderOrganisationDetailsContent(context, organisation);
                }
                context.hideLoading();
            },
            null,
            {
                onError: () => {
                    context.hideLoading();
                    if (context.modal.isConnected) {
                        context.close();
                    }
                }
            }
        );

        return Boolean(succeeded && organisation);
    }

    renderOrganisationDetailsContent(context, organisation) {
        const { body, setTitle } = context;
        const details = document.createElement('article');
        const content = document.createElement('div');
        const title = document.createElement('h2');
        const uid = document.createElement('span');
        const logo = this.createOrganisationModalLogo(organisation);
        const name = String(organisation?.name || organisation?.nom || '').trim();

        setTitle(name || this.translate('organizationModal.dialog'));
        details.className = 'event-details organisation-details';
        content.className = 'event-details-content organisation-details-content';
        title.className = 'event-details-title organisation-details-title';
        title.textContent = name || '—';
        uid.className = 'event-details-uid organisation-details-uid';
        uid.textContent = organisation?.uid || organisation?.uid_organismes || '';
        details.append(logo, title);
        content.append(this.createOrganisationModalMeta(organisation));
        details.append(content);
        body.replaceChildren(uid, details);

        return true;
    }

    cleanupOrganisationDetails() {
        this.pendingOrganisationDetails = null;
        this.activeOrganisationModal = null;
    }

    createOrganisationModalLogo(organisation) {
        const figure = document.createElement('figure');
        const image = document.createElement('img');
        const name = String(organisation?.name || organisation?.nom || '').trim();
        const logoUrl = String(organisation?.logo?.url || '').trim();
        const logoWidth = Number(organisation?.logo?.width);
        const logoHeight = Number(organisation?.logo?.height);
        const logoRatio = logoWidth > 0 && logoHeight > 0
            ? logoWidth / logoHeight
            : 1;

        figure.className = 'calendar-agenda-event-thumbnail calendar-event-modal-image organisation-details-logo';
        if (!logoUrl) {
            figure.classList.add('organisation-details-logo-fallback');
        }
        image.src = logoUrl || '/images/account-grey.svg';
        image.alt = this.translate('organizationModal.logoAlt', { name });
        image.loading = 'eager';
        image.decoding = 'async';

        if (logoWidth > 0 && logoHeight > 0) {
            image.width = logoWidth;
            image.height = logoHeight;
        }

        this.setEventModalImageDimensions(figure, logoRatio);
        image.addEventListener('load', () => {
            const naturalRatio = image.naturalWidth > 0 && image.naturalHeight > 0
                ? image.naturalWidth / image.naturalHeight
                : logoRatio;

            figure.classList.add('calendar-event-modal-image-high-resolution');
            this.setEventModalImageDimensions(figure, naturalRatio);
        });

        if (logoUrl) {
            image.addEventListener('error', () => {
                figure.classList.add('organisation-details-logo-fallback');
                image.src = '/images/account-grey.svg';
                this.setEventModalImageDimensions(figure, 1);
            }, { once: true });
        }
        figure.append(image);

        return figure;
    }

    createOrganisationModalMeta(organisation) {
        const meta = document.createElement('div');
        const address = this.createOrganisationModalAddress(organisation);
        const website = this.createOrganisationModalLink(
            organisation?.website,
            this.translate('organizationModal.websiteAction')
        );
        const email = this.createOrganisationModalLink(
            organisation?.contact?.email ? `mailto:${organisation.contact.email}` : null,
            organisation?.contact?.email
        );
        const phone = this.createOrganisationModalLink(
            organisation?.contact?.phone ? `tel:${organisation.contact.phone}` : null,
            organisation?.contact?.phone
        );
        const fields = [
            organisation?.acronym
                ? this.createEventModalField(this.translate('organizationModal.acronym'), organisation.acronym)
                : null,
            organisation?.short_name
                ? this.createEventModalField(this.translate('organizationModal.shortName'), organisation.short_name)
                : null,
            organisation?.description
                ? this.createEventModalField(this.translate('organizationModal.description'), organisation.description, 'organisation-details-description')
                : null,
            organisation?.country
                ? this.createEventModalField(
                    this.translate('organizationModal.country'),
                    this.getEventCountry({ country: organisation.country })
                )
                : null,
            address
                ? this.createEventModalField(this.translate('organizationModal.address'), address)
                : null,
            organisation?.timezone
                ? this.createEventModalField(this.translate('organizationModal.timezone'), organisation.timezone)
                : null,
            organisation?.primary_language
                ? this.createEventModalField(
                    this.translate('organizationModal.language'),
                    String(organisation.primary_language).toUpperCase()
                )
                : null,
            website
                ? this.createEventModalField(this.translate('organizationModal.website'), website)
                : null,
            email
                ? this.createEventModalField(this.translate('organizationModal.email'), email)
                : null,
            phone
                ? this.createEventModalField(this.translate('organizationModal.phone'), phone)
                : null,
            organisation?.contact?.fax
                ? this.createEventModalField(this.translate('organizationModal.fax'), organisation.contact.fax)
                : null,
            Number.isFinite(Number(organisation?.event_count))
                ? this.createEventModalField(
                    this.translate('organizationModal.events'),
                    String(Number(organisation.event_count))
                )
                : null
        ];

        meta.className = 'calendar-event-modal-meta organisation-details-meta';
        meta.append(...fields.filter(Boolean));

        return meta;
    }

    createOrganisationModalAddress(organisation) {
        const line = [organisation?.address, organisation?.postal_code, organisation?.city]
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .join(', ');

        return line || null;
    }

    createOrganisationModalLink(href, label) {
        const safeHref = String(href || '').trim();
        const safeLabel = String(label || '').trim();

        if (!safeHref || !safeLabel) {
            return null;
        }

        const link = document.createElement('a');

        link.className = 'organisation-details-link';
        link.href = safeHref;
        link.textContent = safeLabel;
        if (/^https?:\/\//i.test(safeHref)) {
            link.target = '_blank';
            link.rel = 'noopener noreferrer external';
        }

        return link;
    }

    createEventModalActions(event, context) {
        const actions = document.createElement('div');
        const definitions = [
            { id: 'validate', labelKey: 'eventValidation.confirmAction', icon: '/images/check-grey.svg' },
            { id: 'update', labelKey: 'eventModal.update', icon: '/images/update-grey.svg' },
            { id: 'signal', labelKey: 'eventModal.signal', icon: '/images/signal-grey.svg' }
        ];

        actions.className = 'calendar-event-modal-actions';
        definitions.forEach(({ id, labelKey, icon }) => {
            if (id === 'validate' && !this.canCurrentUserValidateEvent(event)) {
                return;
            }
            if (id === 'update' && !this.canCurrentUserEditEvent(event)) {
                return;
            }

            const button = document.createElement('button');
            const image = document.createElement('img');
            const text = document.createElement('span');

            button.type = 'button';
            button.className = 'ui-buttons medium square';
            button.dataset.action = id;
            if (id === 'validate') {
                button.addEventListener('click', (clickEvent) => {
                    clickEvent.stopPropagation();
                    this.confirmEventPublication(event, context);
                });
            } else if (id === 'update') {
                button.addEventListener('click', (clickEvent) => {
                    clickEvent.stopPropagation();
                    this.editor.open(event, button);
                });
            } else if (id === 'signal') {
                button.addEventListener('click', (clickEvent) => {
                    clickEvent.stopPropagation();
                    this.reporter.open(event);
                });
            }
            image.src = icon;
            image.alt = '';
            image.loading = 'lazy';
            image.setAttribute('aria-hidden', 'true');
            text.textContent = this.translate(labelKey);
            button.append(image, text);
            actions.append(button);
        });

        return actions;
    }

    canCurrentUserEditEvent(event) {
        return Boolean(this.app?.user) && event?.can_edit === true;
    }

    isCurrentUserMaster() {
        return String(this.app?.user?.class || '').toUpperCase() === 'MASTER';
    }

    canCurrentUserValidateEvent(event) {
        return this.isCurrentUserMaster() && event?.is_pending_validation === true;
    }

    confirmEventPublication(event, context) {
        if (!this.canCurrentUserValidateEvent(event) || this.publicationValidationInProgress) {
            return false;
        }

        const confirmation = new Notification({
            icon: 'warning.svg',
            title: this.translate('eventValidation.confirmTitle'),
            content: this.translate('eventValidation.confirmMessage'),
            wait: true,
            close: true,
            buttons: [
                {
                    label: this.translate('eventValidation.cancel'),
                    class: 'ui-buttons cancel',
                    fct: () => Notification.remove(confirmation, 'now')
                },
                {
                    label: this.translate('eventValidation.confirmAction'),
                    class: 'ui-buttons',
                    fct: () => {
                        Notification.remove(confirmation, 'now');
                        void this.validateEventPublication(event, context);
                    }
                }
            ]
        });

        return true;
    }

    async validateEventPublication(event, context) {
        const uid = String(event?.uid || '');

        if (!uid || this.publicationValidationInProgress) {
            return false;
        }

        this.publicationValidationInProgress = true;
        let updatedEvent = null;

        try {
            const succeeded = await this.api.query(
                'post',
                `/events/validate/${encodeURIComponent(uid)}`,
                {},
                () => context.showLoading(),
                (response) => {
                    updatedEvent = response?.result || null;
                    context.hideLoading();
                },
                null,
                {
                    onError: () => context.hideLoading()
                }
            );

            if (!succeeded || !updatedEvent) {
                return false;
            }

            const eventIndex = this.events.findIndex((item) => String(item?.uid || '') === uid);
            if (eventIndex >= 0) {
                this.events.splice(eventIndex, 1, updatedEvent);
            }
            this.renderMountedViews();
            if (context.modal.isConnected) {
                this.pendingEventDetails.event = updatedEvent;
                this.renderEventDetailsContent(context, updatedEvent);
            }
            new Notification({
                icon: 'check.svg',
                title: this.translate('eventValidation.successTitle'),
                content: this.translate('eventValidation.successMessage'),
                delay: 8000
            });

            return true;
        } finally {
            this.publicationValidationInProgress = false;
        }
    }

    prepareEventModalImage(image, event) {
        const imageElement = image?.querySelector('img');
        const mediumImageUrl = this.getEventMediumImageUrl(event);
        const originalImageUrl = this.getEventImageUrl(event);
        const fallbackImageUrls = this.getEventFallbackImageUrls(event, 'detail');
        const width = Number(event.illustration_width);
        const height = Number(event.illustration_height);
        const ratio = Number(event.illustration_ratio) || (width > 0 && height > 0 ? width / height : null);

        if (!image || !imageElement) {
            return;
        }

        image.classList.add('calendar-event-modal-image');
        image.removeAttribute('aria-hidden');
        imageElement.alt = this.translate('eventModal.posterAlt', { name: this.getLocalizedValue(event.name) });
        imageElement.loading = 'eager';
        imageElement.decoding = 'async';
        this.setEventModalImageDimensions(image, ratio || 1);

        const sources = [...new Set([mediumImageUrl, originalImageUrl, ...fallbackImageUrls].filter(Boolean))];
        const loadHighResolutionImage = (sourceIndex = 0) => {
            const source = sources[sourceIndex];
            const loader = new Image();

            if (!source) {
                return;
            }

            loader.decoding = 'async';
            loader.addEventListener('load', () => {
                const loadedRatio = loader.naturalWidth > 0 && loader.naturalHeight > 0
                    ? loader.naturalWidth / loader.naturalHeight
                    : ratio || 1;

                imageElement.src = source;
                imageElement.width = loader.naturalWidth || width || 1024;
                imageElement.height = loader.naturalHeight || height || 1024;
                image.classList.add('calendar-event-modal-image-high-resolution');
                this.setEventModalImageDimensions(image, loadedRatio);
            }, { once: true });
            loader.addEventListener('error', () => {
                if (sourceIndex + 1 < sources.length) {
                    loadHighResolutionImage(sourceIndex + 1);
                }
            }, { once: true });
            loader.src = source;
        };

        loadHighResolutionImage();
    }

    setEventModalImageDimensions(image, ratio) {
        const availableWidth = Math.max(95, window.innerWidth - 70);
        const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
        const imageWidth = Math.max(1, Math.min(availableWidth, 250 * safeRatio) - 10);
        const imageHeight = imageWidth / safeRatio;

        image.style.setProperty('--calendar-event-image-width', `${imageWidth}px`);
        image.style.setProperty('--calendar-event-image-height', `${imageHeight}px`);
        image.style.setProperty('--calendar-event-image-ratio', String(safeRatio));
    }

    createEventModalMeta(event) {
        const meta = document.createElement('div');
        const organizerName = [
            event.organizer?.name,
            event.location?.organization
        ].find((value) => typeof value === 'string' && value.trim() !== '');
        const organizerValue = organizerName
            ? this.createEventOrganisationLink(event.organizer, organizerName)
            : null;
        const startDate = event.start_date;
        const endDate = event.end_date || startDate;
        const dateFields = startDate === endDate
            ? [
                this.createEventModalField(
                    this.translate('eventModal.date'),
                    this.formatEventModalDate(startDate)
                )
            ]
            : [
                this.createEventModalField(
                    this.translate('eventModal.startDate'),
                    this.formatEventModalDate(startDate)
                ),
                this.createEventModalField(
                    this.translate('eventModal.endDate'),
                    this.formatEventModalDate(endDate)
                )
            ];
        const fields = [
            organizerValue
                ? this.createEventModalField(
                    this.translate('eventModal.organizer'),
                    organizerValue,
                    'calendar-event-modal-organizer'
                )
                : null,
            this.createEventModalLocation(event),
            ...dateFields,
            this.createEventModalExternalLink(event),
            this.createEventModalField(this.translate('eventModal.description'), this.getLocalizedValue(event.description), 'calendar-event-modal-description'),
            this.createEventModalSource(event),
            this.createEventModalTags(event)
        ];

        meta.className = 'calendar-event-modal-meta';
        meta.append(...fields.filter(Boolean));

        return meta;
    }

    createEventOrganisationLink(organisation, label) {
        const uid = String(organisation?.uid || organisation?.uid_organismes || '').trim();

        if (!uid) {
            return String(label || '').trim();
        }

        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'calendar-event-modal-external-link calendar-event-modal-organization-link';
        button.textContent = String(label || '').trim();
        button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.openOrganisationDetails({ ...organisation, uid }, button);
        });

        return button;
    }

    createEventModalTags(event) {
        const tags = this.createAgendaEventTags(event);
        const tagElements = [...tags.querySelectorAll('.calendar-agenda-event-tag')];

        tags.classList.add('calendar-event-modal-types');
        tags.replaceChildren(...tagElements);

        return tags;
    }

    createEventModalField(label, value, className = '') {
        const field = document.createElement('div');
        const labelElement = document.createElement('div');
        const valueElement = document.createElement('div');

        field.className = 'calendar-event-modal-field';
        if (className) {
            field.classList.add(className);
        }
        labelElement.className = 'calendar-event-modal-field-label';
        valueElement.className = 'calendar-event-modal-field-value';
        labelElement.textContent = label;
        if (value instanceof Node) {
            valueElement.append(value);
        } else {
            valueElement.textContent = value || '—';
        }
        field.append(labelElement, valueElement);

        return field;
    }

    createEventModalLocation(event) {
        const address = String(event.address || event.location?.address || '').trim();
        const city = String(event.city || event.location?.city || '').trim();
        const country = String(this.getEventCountry(event) || '').trim();
        const mapsAddress = this.formatEventAddress(event);
        const lines = document.createElement('div');
        const addressBlock = document.createElement(mapsAddress ? 'a' : 'div');

        if (!address && !city && !country) {
            return null;
        }

        lines.className = 'calendar-event-modal-location-lines';
        addressBlock.className = 'calendar-event-modal-location-address';
        if (mapsAddress) {
            addressBlock.classList.add('calendar-event-modal-location-link');
            addressBlock.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsAddress)}`;
            addressBlock.target = '_blank';
            addressBlock.rel = 'noopener noreferrer';
            addressBlock.setAttribute('aria-label', this.translate('eventModal.openMaps'));
        }

        if (address) {
            const addressText = document.createElement('span');

            addressText.className = 'calendar-event-modal-location-address-text';
            addressText.textContent = address;
            addressBlock.append(addressText);
        }

        if (city || country) {
            const placeLine = document.createElement('div');

            placeLine.className = 'calendar-event-modal-location-line';
            if (city) {
                const cityText = document.createElement('span');

                cityText.className = 'calendar-event-modal-location-city';
                cityText.textContent = city;
                placeLine.append(cityText);
            }
            if (city && country) {
                placeLine.append(document.createTextNode(', '));
            }
            if (country) {
                const countryText = document.createElement('span');

                countryText.className = 'calendar-event-modal-location-country';
                countryText.textContent = country;
                placeLine.append(countryText);
            }
            addressBlock.append(placeLine);
        }

        lines.append(addressBlock);

        return this.createEventModalField(
            this.translate('eventModal.location'),
            lines,
            'calendar-event-modal-location'
        );
    }

    createEventModalExternalLink(event) {
        const link = String(event.external_link || '').trim();
        if (!/^https?:\/\//i.test(link)) return null;

        const anchor = document.createElement('a');

        anchor.className = 'calendar-event-modal-external-link';
        anchor.href = link;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer external';
        anchor.textContent = this.translate('eventModal.externalLinkAction');

        return this.createEventModalField(
            this.translate('eventModal.externalLink'),
            anchor,
            'calendar-event-modal-external'
        );
    }

    createEventModalSource(event) {
        const source = String(event.source || '').trim();
        if (!source) return null;

        let value = source;
        if (/^https?:\/\//i.test(source)) {
            const anchor = document.createElement('a');

            anchor.className = 'calendar-event-modal-source-link';
            anchor.href = source;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer external';
            anchor.textContent = source;
            value = anchor;
        }

        return this.createEventModalField(
            this.translate('eventModal.source'),
            value,
            'calendar-event-modal-source'
        );
    }

    getEventImageUrl(event) {
        const image = this.getEventImageReference(event);

        if (!image) {
            return this.getEventFallbackImageUrl(event, 'detail');
        }

        if (/^(https?:)?\/\//.test(image) || image.startsWith('/')) {
            return image;
        }

        return `/uploads/${image}`;
    }

    getEventMiniImageUrl(event) {
        const image = this.getEventImageReference(event);

        if (!image) {
            return this.getEventFallbackImageUrl(event, 'mini');
        }

        const suffixMini = (url) => url.replace(
            /(\.[a-z0-9]+)([?#].*)?$/i,
            '-mini.webp$2'
        );

        if (image.startsWith('/uploads/')) {
            return suffixMini(image);
        }
        if (/^(https?:)?\/\//.test(image) || image.startsWith('/')) {
            return image;
        }

        return `/uploads/${suffixMini(image)}`;
    }

    getEventMediumImageUrl(event) {
        const image = this.getEventImageReference(event);

        if (!image) {
            return this.getEventFallbackImageUrl(event, 'detail');
        }

        const suffixMedium = (url) => url.replace(
            /(\.[a-z0-9]+)([?#].*)?$/i,
            '-medium.webp$2'
        );

        if (image.startsWith('/uploads/')) {
            return suffixMedium(image);
        }
        if (/^(https?:)?\/\//.test(image) || image.startsWith('/')) {
            return image;
        }

        return `/uploads/${suffixMedium(image)}`;
    }

    getEventImageReference(event) {
        const source = String(event.illustration_source || '').trim().toLowerCase();
        if (['organizer_logo', 'federation_logo', 'discipline_fallback'].includes(source)) {
            return '';
        }

        const image = event.illustration
            || event.image
            || event.image_url
            || event.picture
            || event.poster
            || '';

        return typeof image === 'string' ? image.trim() : '';
    }

    getEventFallbackImageUrl(event, variant = 'detail') {
        return this.getEventFallbackImageUrls(event, variant)[0] || '';
    }

    getEventFallbackImageUrls(event, variant = 'detail') {
        return [...new Set([
            this.getEventOrganizerLogoUrl(event),
            this.getEventDisciplineFallbackImageUrl(event, variant)
        ].filter(Boolean))];
    }

    getEventOrganizerLogoUrl(event) {
        const logo = event?.organizer?.logo;
        const url = typeof logo === 'string'
            ? logo
            : (logo?.url || logo?.local_path || logo?.source_url || event?.organizer?.logo_url || '');

        return String(url || '').trim();
    }

    getEventDisciplineFallbackImageUrl(event, variant = 'detail') {
        const disciplines = this.getEventDisciplines(event);
        const discipline = disciplines.length === 1 ? disciplines[0] : 'multi';
        const suffix = variant === 'mini' ? '-mini' : '';

        return `/uploads/event-fallback-${discipline}${suffix}.webp`;
    }

    bindEventImageFallback(image, event, variant = 'detail', container = null) {
        const fallbacks = this.getEventFallbackImageUrls(event, variant);
        let fallbackIndex = 0;
        const absoluteUrl = (value) => {
            try {
                return new URL(value, window.location.href).href;
            } catch (error) {
                return String(value || '');
            }
        };

        image.addEventListener('error', () => {
            const currentSource = absoluteUrl(image.getAttribute('src'));
            while (
                fallbackIndex < fallbacks.length
                && absoluteUrl(fallbacks[fallbackIndex]) === currentSource
            ) {
                fallbackIndex += 1;
            }
            const fallback = fallbacks[fallbackIndex];
            if (!fallback) return;

            const dimension = variant === 'mini' ? 336 : 1024;

            fallbackIndex += 1;
            image.width = dimension;
            image.height = dimension;
            image.src = fallback;
            container?.style.setProperty('--calendar-event-image-ratio', '1');
        });
    }

    formatEventModalDate(dateKey) {
        const date = this.parseDate(dateKey);
        return date ? this.formatFullDate(date) : '';
    }

    formatEventAddress(event) {
        return [
            event.address || event.location?.address,
            event.city || event.location?.city,
            this.getEventCountry(event)
        ].filter(Boolean).join(', ');
    }

    formatEventLocationPlace(event) {
        return [
            event.city || event.location?.city,
            this.getEventCountry(event)
        ].filter(Boolean).join(', ');
    }

    closeEventModal() {
        const activeModal = this.activeEventModal;

        if (!activeModal) {
            return false;
        }

        this.app?.simpleModal?.close(activeModal);
        return true;
    }

    toggleFilterValue(filterType, value, button = null) {
        const values = this.filters[filterType];

        if (!(values instanceof Set)) {
            return false;
        }

        if (values.has(value)) {
            values.delete(value);
        } else {
            values.add(value);
        }

        void this.persistFilters();
        const active = values.has(value);

        button?.classList.toggle('active', active);
        button?.setAttribute('aria-pressed', String(active));
        const filterModal = this.viewContainers.get('filters')?.closest('.home-events-modal-filters');
        const currentResults = filterModal?.querySelector('.calendar-filter-results');

        if (currentResults) {
            const nextResults = this.createFilterResultsCount();

            nextResults.classList.toggle(
                'home-filter-results-fixed',
                currentResults.classList.contains('home-filter-results-fixed')
            );
            currentResults.replaceWith(nextResults);
        }
        this.app?.home?.updateFilterBadge?.();
        return true;
    }

    selectDate(date, options = {}) {
        this.selectedDate = new Date(date);
        this.focusedYear = this.selectedDate.getFullYear();
        this.updateActiveDateButton();
        if (options.renderList !== false) {
            this.replaceAgendaList();
        }

        if (options.scroll) {
            this.scrollSelectedDateIntoView('smooth');
        }
    }

    selectDateByKey(dateKey, options = {}) {
        const date = this.parseDate(dateKey);

        if (!date) {
            return;
        }

        this.selectDate(date, options);
    }

    updateActiveDateButton() {
        const selectedKey = this.formatDateKey(this.selectedDate);

        this.viewContainers.forEach((container) => {
            container.querySelectorAll('.calendar-date-button, .calendar-timeline-day').forEach((button) => {
                button.classList.toggle('active', button.dataset.date === selectedKey);
            });
        });
    }

    replaceAgendaList() {
        const container = this.viewContainers.get('list');
        const agenda = container?.querySelector('.calendar-agenda');

        if (agenda) {
            this.cleanupAgendaProgressiveLoading(agenda);
            const nextAgenda = this.createAgendaList({
                includeOrganization: false,
                mergeTagGroups: true
            });

            agenda.replaceWith(nextAgenda);
            this.scheduleAgendaTodayAlignment(container);
            this.initializeAgendaProgressiveLoading(container, nextAgenda);
        }
    }

    scheduleAgendaTodayAlignment(container, behavior = 'auto') {
        if (!container) {
            return false;
        }

        const align = () => this.scrollAgendaTodayIntoView(container, behavior);

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(align);
        });
        if (document.fonts?.ready) {
            document.fonts.ready.then(() => window.requestAnimationFrame(align));
        }

        return true;
    }

    scrollAgendaTodayIntoView(container, behavior = 'auto') {
        const agenda = container.querySelector('.calendar-agenda');
        const todayKey = this.formatDateKey(this.getToday());
        const todaySection = agenda?.querySelector(`.calendar-agenda-day[data-date="${todayKey}"]`);
        const todayAnchor = agenda?.querySelector('.calendar-agenda-today-anchor');
        const target = todaySection
            || todayAnchor;
        const usesViewportScroll = container.matches('.home-events-view-list');
        const scroller = usesViewportScroll
            ? document.scrollingElement
            : container.closest('.app-modal__scroll') || agenda;

        if (!target || !scroller) {
            return false;
        }

        const scrollerRect = usesViewportScroll
            ? { top: 0 }
            : scroller.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const maximum = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
        const topOffset = container.closest('.home-events-view-list') ? 22 : 2;
        const targetTop = scroller.scrollTop
            + targetRect.top
            - scrollerRect.top
            - topOffset;
        const scrollTop = Math.max(0, Math.min(maximum, targetTop));

        if (behavior === 'auto') {
            scroller.scrollTop = scrollTop;
        } else {
            scroller.scrollTo({ top: scrollTop, behavior });
        }

        return true;
    }

    scrollSelectedDateIntoView(behavior = 'auto') {
        const timeline = this.container.querySelector('.calendar-timeline');

        if (timeline) {
            this.updateTimelineActiveStates(timeline, this.selectedDate);
            this.syncTimelineToDate(timeline, this.selectedDate, behavior);
            return;
        }

        const scroller = this.container.querySelector('.calendar-date-scroller');
        const button = this.container.querySelector(`.calendar-date-button[data-date="${this.formatDateKey(this.selectedDate)}"]`);

        if (!scroller || !button) {
            return;
        }

        const target = button.offsetLeft + (button.offsetWidth / 2) - (scroller.clientWidth / 2);

        if (behavior === 'auto') {
            scroller.scrollLeft = Math.max(target, 0);
        } else {
            scroller.scrollTo({ left: Math.max(target, 0), behavior });
        }

        this.correctSelectedDateAlignment(scroller, button);
        this.queueDateScaleUpdate(scroller);
    }

    scrollMonthIntoView(date, behavior = 'auto') {
        const timeline = this.container.querySelector('.calendar-timeline');

        if (!timeline) {
            this.scrollSelectedDateIntoView(behavior);
            return;
        }

        this.updateTimelineActiveStates(timeline, date);
        this.syncTimelineToDate(timeline, date, behavior);
    }

    centerTimelineElement(scroller, element, behavior = 'auto') {
        const scrollerRect = scroller.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const delta = (elementRect.left + (elementRect.width / 2))
            - (scrollerRect.left + (scrollerRect.width / 2));
        const target = Math.max(0, scroller.scrollLeft + delta);

        if (behavior === 'auto') {
            const inlineScrollBehavior = scroller.style.scrollBehavior;

            scroller.style.scrollBehavior = 'auto';
            scroller.scrollLeft = target;
            scroller.style.scrollBehavior = inlineScrollBehavior;
        } else if (Math.abs(delta) > scroller.clientWidth * 0.75) {
            const approachDistance = Math.min(scroller.clientWidth * 0.72, 720);
            const inlineScrollBehavior = scroller.style.scrollBehavior;

            scroller.style.scrollBehavior = 'auto';
            scroller.scrollLeft = Math.max(0, target - (Math.sign(delta) * approachDistance));
            scroller.style.scrollBehavior = inlineScrollBehavior;
            window.requestAnimationFrame(() => {
                scroller.scrollTo({ left: target, behavior });
            });
        } else {
            scroller.scrollTo({ left: target, behavior });
        }
    }

    correctSelectedDateAlignment(scroller, button) {
        const scrollerRect = scroller.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        const delta = (buttonRect.left + (buttonRect.width / 2)) - (scrollerRect.left + (scrollerRect.width / 2));

        if (Math.abs(delta) > 0.5) {
            scroller.scrollLeft += delta;
        }
    }

    scheduleSelectedDateAlignment(target = 'day') {
        const align = () => {
            if (target === 'month') {
                this.scrollMonthIntoView(this.getToday(), 'auto');
            } else {
                this.scrollSelectedDateIntoView('auto');
            }
        };

        window.requestAnimationFrame(() => {
            align();
        });

        if (document.fonts?.ready) {
            document.fonts.ready.then(() => {
                align();
            });
        }
    }

    queueDateScaleUpdate(scroller) {
        if (this.dateScrollFrame) {
            window.cancelAnimationFrame(this.dateScrollFrame);
        }

        this.dateScrollFrame = window.requestAnimationFrame(() => {
            this.updateDateNavigatorScales(scroller);
        });
    }

    updateDateNavigatorScales(scroller) {
        const center = scroller.scrollLeft + (scroller.clientWidth / 2);
        const range = Math.max(scroller.clientWidth * 0.42, 160);

        scroller.querySelectorAll('.calendar-date-button').forEach((button) => {
            const buttonCenter = button.offsetLeft + (button.offsetWidth / 2);
            const distance = Math.min(Math.abs(buttonCenter - center) / range, 1);
            const scale = 1.18 - (distance * 0.34);
            const opacity = 1 - (distance * 0.32);

            button.style.setProperty('--date-scale', scale.toFixed(3));
            button.style.setProperty('--date-opacity', opacity.toFixed(3));
        });
    }

    getCenteredDateKey(scroller) {
        const center = scroller.scrollLeft + (scroller.clientWidth / 2);
        let closestButton = null;
        let closestDistance = Infinity;

        scroller.querySelectorAll('.calendar-date-button').forEach((button) => {
            const buttonCenter = button.offsetLeft + (button.offsetWidth / 2);
            const distance = Math.abs(buttonCenter - center);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestButton = button;
            }
        });

        return closestButton?.dataset?.date || null;
    }

    getAgendaOccurrences() {
        const events = this.getFilteredEvents();
        const eventsByDate = this.getEventOccurrencesByDate(events);
        const todayKey = this.formatDateKey(this.getToday());

        return [...eventsByDate.entries()]
            .filter(([dateKey]) => dateKey >= todayKey)
            .map(([dateKey, dayEvents]) => ({
                date: this.parseDate(dateKey),
                events: dayEvents
            }));
    }

    getEventOccurrencesByDate(events) {
        const eventsByDate = new Map();
        const orderedEvents = [...events].sort((eventA, eventB) => (
            this.compareEventValue(eventA.start_date, eventB.start_date)
            || this.compareEventValue(this.getLocalizedValue(eventA.name), this.getLocalizedValue(eventB.name))
        ));

        orderedEvents.forEach((event) => {
            const startDate = this.parseDate(event.start_date);
            const endDate = this.parseDate(event.end_date || event.start_date);

            if (!startDate || !endDate || endDate < startDate) {
                return;
            }

            const currentDate = new Date(startDate);

            while (currentDate <= endDate) {
                const dateKey = this.formatDateKey(currentDate);
                const dayEvents = eventsByDate.get(dateKey) || [];

                dayEvents.push(event);
                eventsByDate.set(dateKey, dayEvents);
                currentDate.setDate(currentDate.getDate() + 1);
            }
        });

        return eventsByDate;
    }

    getEventsForDateFromList(date, events) {
        const isoDate = this.formatDateKey(date);
        const orderedEvents = this.getOrderedEventsMap(events);

        return events.filter((event) => {
            const startDate = event.start_date;
            const endDate = event.end_date || startDate;

            return startDate <= isoDate && endDate >= isoDate;
        }).sort((eventA, eventB) => (
            (orderedEvents.get(this.getEventId(eventA)) || 0) - (orderedEvents.get(this.getEventId(eventB)) || 0)
        ));
    }

    getOrderedEventsMap(events) {
        return new Map([...events]
            .sort((eventA, eventB) => (
                this.compareEventValue(eventA.start_date, eventB.start_date)
                || this.compareEventValue(this.getLocalizedValue(eventA.name), this.getLocalizedValue(eventB.name))
            ))
            .map((event, index) => [this.getEventId(event), index + 1]));
    }

    getFilteredEvents() {
        const events = this.getEventsMatchingCountryFilters(this.getEventsMatchingTagFilters());
        const validationEvent = this.validationEventUid
            ? this.events.find((event) => String(event?.uid || '') === this.validationEventUid)
            : null;

        if (validationEvent && !events.includes(validationEvent)) {
            return [...events, validationEvent];
        }

        return events;
    }

    endEventValidationContext() {
        this.validationEventUid = null;
    }

    getEventsMatchingTagFilters() {
        const activeDisciplines = [...this.filters.disciplines];
        const activeAdditionalTags = this.getActiveAdditionalTagFilters();

        return this.events.filter((event) => {
            const eventTags = Array.isArray(event.tags) ? event.tags.map((tag) => String(tag).toLowerCase()) : [];
            const hasDiscipline = activeDisciplines.length === 0 || activeDisciplines.some((tag) => eventTags.includes(tag));
            const hasAdditionalTag = activeAdditionalTags.length === 0 || activeAdditionalTags.some((tag) => (
                eventTags.includes(tag) || this.eventMatchesStatusFilter(event, tag)
            ));

            return hasDiscipline && hasAdditionalTag;
        });
    }

    getEventsMatchingCountryFilters(events) {
        if (this.filters.countries.size === 0) {
            return events;
        }

        return events.filter((event) => this.filters.countries.has(this.getEventCountry(event)));
    }

    getCountryOptions(events = this.events) {
        return [...new Set(events.map((event) => this.getEventCountry(event)).filter(Boolean))]
            .sort((countryA, countryB) => countryA.localeCompare(countryB, this.locale));
    }

    getCountryFilterTags() {
        return this.getCountryOptions().map((country) => ({
            id: country,
            label: country,
            color: '#667481'
        }));
    }

    getDisciplineFilterTags() {
        const availableTags = new Set(this.events.flatMap((event) => event.tags || []).map((tag) => String(tag).toLowerCase()));

        return this.disciplines
            .filter((discipline) => availableTags.has(discipline))
            .map((discipline) => ({
                id: discipline,
                label: this.disciplineLabels[discipline],
                color: this.getDisciplineTagColor(discipline)
            }));
    }

    getAdditionalFilterTags() {
        const availableTags = new Set(this.events.flatMap((event) => event.tags || []).map((tag) => String(tag).toLowerCase()));
        const configuredTags = [...this.getConfiguredEventTags().values()]
            .filter((tag) => availableTags.has(String(tag.id).toLowerCase()))
            .map((tag) => ({
                id: String(tag.id).toLowerCase(),
                label: this.getLocalizedValue(tag.label),
                color: tag.color
            }));

        return [...configuredTags, ...this.getStatusFilterTags()];
    }

    getActiveFilterCount() {
        return this.filters.disciplines.size
            + this.getActiveAdditionalTagFilters().length
            + this.filters.countries.size;
    }

    getActiveAdditionalTagFilters() {
        return [...this.filters.additionalTags].filter((tag) => (
            (tag !== 'draft' || Boolean(this.app?.user))
            && (tag !== 'pending_validation' || this.isCurrentUserMaster())
        ));
    }

    getStatusFilterTags() {
        const tags = [this.getEventStatusTag('public_draft')];

        if (this.isCurrentUserMaster()) {
            tags.push(this.getEventStatusTag('pending_validation'));
        }
        if (this.app?.user) {
            tags.push(this.getEventStatusTag('draft'));
        }

        return tags.filter(Boolean);
    }

    getEventStatusTag(status) {
        if (status === 'draft' && !this.app?.user) {
            return null;
        }
        if (status === 'draft') {
            return {
                id: 'draft',
                label: this.translate('eventStatus.draft'),
                color: '#6f7e8b',
                type: 'additional'
            };
        }
        if (status === 'public_draft') {
            return {
                id: 'public_draft',
                label: this.translate('eventStatus.publicDraft'),
                color: '#b07a2a',
                type: 'additional'
            };
        }
        if (status === 'pending_validation' && this.isCurrentUserMaster()) {
            return {
                id: 'pending_validation',
                label: this.translate('eventStatus.pendingValidation'),
                color: '#b07a2a',
                type: 'additional'
            };
        }

        return null;
    }

    eventMatchesStatusFilter(event, filter) {
        if (filter === 'pending_validation') {
            return this.isCurrentUserMaster() && event?.is_pending_validation === true;
        }
        if (filter === 'public_draft') {
            return event?.status === filter && event?.is_pending_validation !== true;
        }

        return filter === 'draft' && event?.status === filter;
    }

    getAvailableYears() {
        const years = new Set([this.getToday().getFullYear(), this.focusedYear]);

        [this.firstDate, this.lastDate].forEach((date) => {
            if (date) {
                years.add(date.getFullYear());
            }
        });
        this.events.forEach((event) => {
            const startDate = this.parseDate(event.start_date);
            const endDate = this.parseDate(event.end_date || event.start_date);

            if (startDate) {
                years.add(startDate.getFullYear());
            }
            if (endDate) {
                years.add(endDate.getFullYear());
            }
        });

        return [...years].sort((yearA, yearB) => yearA - yearB);
    }

    getYearDays(year) {
        const days = [];
        const currentDate = new Date(year, 0, 1);

        while (currentDate.getFullYear() === year) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    }

    getDateNavigatorDays() {
        const years = this.getAvailableYears();
        const firstYear = years[0] || this.focusedYear;
        const lastYear = years[years.length - 1] || firstYear;
        const days = [];

        for (let year = firstYear; year <= lastYear; year += 1) {
            days.push(...this.getYearDays(year));
        }

        return days;
    }

    getLatestEventDate(events) {
        return events.reduce((latestDate, event) => {
            const eventDate = this.parseDate(event.end_date || event.start_date);

            if (!eventDate) {
                return latestDate;
            }

            return !latestDate || eventDate > latestDate ? eventDate : latestDate;
        }, null);
    }

    getEarliestEventDate(events) {
        return events.reduce((earliestDate, event) => {
            const eventDate = this.parseDate(event.start_date);

            if (!eventDate) {
                return earliestDate;
            }

            return !earliestDate || eventDate < earliestDate ? eventDate : earliestDate;
        }, null);
    }

    getEventDisciplines(event) {
        const tags = Array.isArray(event.tags) ? event.tags.map((tag) => String(tag).toLowerCase()) : [];

        return this.disciplines.filter((discipline) => tags.includes(discipline));
    }

    getEventCountry(event) {
        const value = event.country || event.location?.country || '';
        const code = String(value).trim().toUpperCase();
        const country = this.app.config?.countries?.list?.find(
            (item) => String(item?.['iso-3166-1-alpha-2'] || '').toUpperCase() === code
        );

        return country?.label || value;
    }

    getEventId(event) {
        return event.uid || `${event.start_date}-${this.getLocalizedValue(event.name)}`;
    }

    getEventDetailKey(event, date) {
        return `${this.getEventId(event)}-${this.formatDateKey(date)}`;
    }

    getTornMaskOffset(key) {
        const hash = [...String(key)].reduce((value, character) => (
            ((value << 5) - value + character.charCodeAt(0)) | 0
        ), 0);

        return -Math.abs(hash % 1900);
    }

    getToday() {
        const today = new Date();

        today.setHours(0, 0, 0, 0);

        return today;
    }

    normalizeSelectedDate() {
        if (!(this.selectedDate instanceof Date) || Number.isNaN(this.selectedDate.getTime())) {
            this.selectedDate = this.getToday();
        }

        this.selectedDate.setHours(0, 0, 0, 0);
        this.focusedYear = this.selectedDate.getFullYear();
    }

    formatAgendaDayTitle(date) {
        if (this.formatDateKey(date) === this.formatDateKey(this.getToday())) {
            return 'Aujourd’hui';
        }

        return new Intl.DateTimeFormat(this.locale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
    }

    createChronologicalList() {
        const list = document.createElement('div');
        const days = this.getCalendarDays();

        list.className = 'calendar-events-list';

        if (days.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'calendar-empty';
            empty.textContent = this.getEmptyEventsMessage();
            list.append(empty);

            return list;
        }

        const layout = this.getCalendarLayout(days);
        const groups = this.getCalendarGroups(days, layout.events);

        groups.forEach((group) => {
            list.append(this.createListGroup(group, layout));
        });

        return list;
    }

    createListGroup(group, layout) {
        const groupElement = document.createElement('section');
        const dates = document.createElement('div');
        const content = document.createElement('div');
        const track = document.createElement('div');
        const groupLaneCount = this.getGroupLaneCount(group.events);

        groupElement.className = 'calendar-list-group';
        groupElement.classList.add(group.startRow % 2 === 0 ? 'calendar-list-group-even' : 'calendar-list-group-odd');
        dates.className = 'calendar-list-group-dates';
        content.className = 'calendar-list-group-content';
        track.className = 'calendar-list-group-track';
        dates.style.setProperty('--calendar-group-rows', group.days.length);
        track.style.setProperty('--calendar-group-rows', group.days.length);
        track.style.setProperty('--calendar-event-lanes', Math.max(groupLaneCount, 1));

        group.days.forEach(({ date }, index) => {
            const dateElement = this.createListDate(date);
            const dayIndex = group.startRow + index - 1;

            dateElement.classList.add(dayIndex % 2 === 0 ? 'calendar-list-date-even' : 'calendar-list-date-odd');
            dates.append(dateElement);
            track.append(this.createListGroupRowBackground(index + 1, dayIndex));
        });

        group.events.forEach(({ event, lane, rowSpan, startRow }) => {
            const eventElement = this.createListEvent(event, rowSpan);

            eventElement.style.gridRow = `${startRow - group.startRow + 1} / span ${rowSpan}`;
            eventElement.style.gridColumn = `${lane + 2}`;
            track.append(eventElement);
        });

        content.append(track);
        groupElement.append(dates, content);

        return groupElement;
    }

    createListGroupRowBackground(row, dayIndex) {
        const background = document.createElement('div');

        background.className = 'calendar-list-group-row-background';
        background.classList.add(dayIndex % 2 === 0 ? 'calendar-list-group-row-background-even' : 'calendar-list-group-row-background-odd');
        background.style.top = `calc(${row - 1} * var(--calendar-row-height))`;

        return background;
    }

    createListDate(date) {
        const dateElement = document.createElement('div');
        const dayName = document.createElement('div');
        const day = document.createElement('div');
        const month = document.createElement('div');
        const year = document.createElement('div');

        dateElement.className = 'calendar-list-date';
        if (this.isWeekend(date)) {
            dateElement.classList.add('calendar-list-date-weekend');
        }
        dayName.className = 'calendar-list-date-day-name';
        day.className = 'calendar-list-date-day';
        month.className = 'calendar-list-date-month';
        year.className = 'calendar-list-date-year';

        dayName.textContent = this.formatWeekday(date);
        day.textContent = this.formatDateDay(date);
        month.textContent = this.formatDateMonth(date);
        year.textContent = this.formatDateYear(date);
        dateElement.append(dayName, day, month, year);

        return dateElement;
    }

    createListEvent(event, rowSpan = 1) {
        const item = document.createElement('article');
        const body = document.createElement('div');
        const name = document.createElement('h2');
        const location = document.createElement('div');
        const meta = document.createElement('div');
        const description = document.createElement('p');
        const disciplineTags = this.createListEventTags(event, 'discipline');
        const additionalTags = this.createListEventTags(event, 'additional');
        const discipline = this.getEventDiscipline(event);

        item.className = 'calendar-list-event';
        item.style.setProperty('--calendar-event-row-span', rowSpan);
        item.style.setProperty('--calendar-event-description-lines', Math.max(3, (rowSpan * 8) - 3));
        body.className = 'calendar-list-event-body';
        name.className = 'calendar-list-event-name';
        location.className = 'calendar-list-event-location';
        meta.className = 'calendar-list-event-meta';
        description.className = 'calendar-list-event-description';

        const nameLink = document.createElement('a');

        nameLink.href = this.getEventPublicPath(event);
        nameLink.textContent = this.getLocalizedValue(event.name);
        name.append(nameLink);
        this.appendEventLocation(location, event);
        meta.textContent = this.getEventMeta(event);
        description.textContent = this.getLocalizedValue(event.description);

        if (discipline) {
            item.dataset.discipline = discipline;
            item.style.setProperty(
                '--calendar-list-event-color',
                `var(${this.disciplineColorVariables[discipline]})`
            );
        }

        if (disciplineTags) {
            body.append(disciplineTags);
        }

        body.append(name);

        if (location.textContent) {
            body.append(location);
        }

        if (meta.textContent) {
            body.append(meta);
        }

        if (description.textContent) {
            body.append(description);
        }

        if (additionalTags) {
            body.append(additionalTags);
        }

        item.append(body);

        return item;
    }

    getEventPublicPath(event) {
        const locale = this.locale || this.app?.home?.locale || 'fr';
        const slug = this.app?.home?.getEventSlug?.(event, locale)
            || event?.canonical_name?.[locale]
            || event?.uid
            || 'event';

        return `/${locale}/events/${encodeURIComponent(String(slug))}`;
    }

    createListEventTags(event, type = 'all') {
        const tags = document.createElement('div');
        const displayTags = this.getDisplayTags(event.tags || [], event).filter((tag) => (
            type === 'all'
            || (type === 'discipline' && tag.type === 'discipline')
            || (type === 'additional' && tag.type === 'additional')
        ));
        tags.className = 'calendar-list-event-tags';
        tags.classList.add(`calendar-list-event-tags-${type}`);

        if (displayTags.length === 0) {
            return null;
        }

        displayTags.forEach(({ id, label, color }) => {
            const tagElement = document.createElement('span');
            tagElement.className = 'calendar-list-event-tag';
            tagElement.dataset.tag = id;
            tagElement.textContent = label;
            if (color) {
                tagElement.style.setProperty('--calendar-list-event-tag-color', color);
            }
            tags.append(tagElement);
        });

        return tags;
    }

    getDisplayTags(tags, event = null) {
        const seen = new Set();
        const configuredTags = this.getConfiguredEventTags();

        const displayTags = tags.reduce((result, tag) => {
            const id = String(tag).toLowerCase();

            if (SUPPORTED_DISCIPLINES.includes(id) && !this.disciplines.includes(id)) {
                return result;
            }

            const configuredTag = configuredTags.get(id);
            const disciplineColor = this.getDisciplineTagColor(id);
            const label = disciplineColor ? this.disciplineLabels[id] : this.getLocalizedValue(configuredTag?.label);
            const color = disciplineColor || configuredTag?.color;
            const type = disciplineColor ? 'discipline' : 'additional';

            if (!label || seen.has(id)) {
                return result;
            }

            seen.add(id);
            result.push({ id, label, color, type });

            return result;
        }, []);

        const statusTag = event?.is_pending_validation === true
            ? this.getEventStatusTag('pending_validation')
            : this.getEventStatusTag(event?.status);
        if (statusTag && !seen.has(statusTag.id)) {
            displayTags.push(statusTag);
        }

        return displayTags;
    }

    getConfiguredEventTags() {
        const tags = this.app?.config?.event_tags?.list;

        return Array.isArray(tags)
            ? new Map(tags.map((tag) => [String(tag.id).toLowerCase(), tag]))
            : new Map();
    }

    getDisciplineTagColor(id) {
        const colorVariable = this.disciplineColorVariables[id];

        return colorVariable ? `var(${colorVariable})` : null;
    }

    getInclusiveDaySpan(startDate, endDate) {
        return this.getEventDayIndex(startDate, endDate) + 1;
    }

    getEventDayIndex(startDate, currentDate) {
        const millisecondsPerDay = 24 * 60 * 60 * 1000;

        return Math.max(0, Math.round((this.parseDateKey(currentDate) - this.parseDateKey(startDate)) / millisecondsPerDay));
    }

    parseDateKey(dateKey) {
        const [year, month, day] = dateKey.split('-').map(Number);

        return Date.UTC(year, month - 1, day);
    }

    getEventDiscipline(event) {
        const tags = Array.isArray(event.tags) ? event.tags.map((tag) => String(tag).toLowerCase()) : [];

        return this.disciplines.find((discipline) => tags.includes(discipline)) || null;
    }

    getEventsForDate(date) {
        const isoDate = this.formatDateKey(date);

        return this.events.filter((event) => {
            const startDate = event.start_date;
            const endDate = event.end_date || startDate;

            return startDate <= isoDate && endDate >= isoDate;
        }).sort((eventA, eventB) => this.compareEventsForCalendar(eventA, eventB));
    }

    getChronologicalEvents() {
        return [...this.events].sort((eventA, eventB) => (
            this.compareEventValue(eventA.start_date, eventB.start_date)
            || this.compareEventValue(this.getLocalizedValue(eventA.name), this.getLocalizedValue(eventB.name))
        ));
    }

    getCalendarDays() {
        const events = this.getChronologicalEvents();

        if (events.length === 0) {
            return [];
        }

        const startDate = this.parseDate(events[0].start_date);
        const endDate = events.reduce((maxDate, event) => {
            const eventEndDate = this.parseDate(event.end_date || event.start_date);

            return eventEndDate && eventEndDate > maxDate ? eventEndDate : maxDate;
        }, startDate);
        const days = [];
        const currentDate = new Date(startDate);

        while (currentDate <= endDate) {
            const date = new Date(currentDate);
            days.push({
                date,
                events: this.getEventsForDate(date)
            });
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    }

    getCalendarLayout(days) {
        const dateRows = new Map();

        days.forEach(({ date }, index) => {
            dateRows.set(this.formatDateKey(date), index + 1);
        });

        const laneEndDates = [];
        const events = this.getChronologicalEvents()
            .map((event) => {
                const startRow = dateRows.get(event.start_date);

                if (!startRow) {
                    return null;
                }

                const endDate = event.end_date || event.start_date;
                const rowSpan = Math.min(
                    this.getEventDuration(event),
                    Math.max(days.length - startRow + 1, 1)
                );
                const lane = this.getAvailableEventLane(laneEndDates, event.start_date);

                laneEndDates[lane] = endDate;

                return {
                    event,
                    lane,
                    startRow,
                    rowSpan
                };
            })
            .filter(Boolean);

        return {
            dateRows,
            events,
            laneCount: laneEndDates.length
        };
    }

    getCalendarGroups(days, eventLayouts) {
        const eventsByStartRow = this.groupEventsByStartRow(eventLayouts);
        const groups = [];
        let row = 1;

        while (row <= days.length) {
            const startRow = row;
            let endRow = row;
            let cursor = row;

            while (cursor <= endRow) {
                const startingEvents = eventsByStartRow.get(cursor) || [];
                startingEvents.forEach((eventLayout) => {
                    endRow = Math.max(endRow, eventLayout.startRow + eventLayout.rowSpan - 1);
                });
                cursor += 1;
            }

            groups.push({
                startRow,
                endRow,
                days: days.slice(startRow - 1, endRow),
                events: eventLayouts.filter(({ startRow: eventStartRow }) => eventStartRow >= startRow && eventStartRow <= endRow)
            });
            row = endRow + 1;
        }

        return groups;
    }

    groupEventsByStartRow(eventLayouts) {
        return eventLayouts.reduce((eventsByStartRow, eventLayout) => {
            const events = eventsByStartRow.get(eventLayout.startRow) || [];
            events.push(eventLayout);
            eventsByStartRow.set(eventLayout.startRow, events);

            return eventsByStartRow;
        }, new Map());
    }

    getGroupLaneCount(eventLayouts) {
        if (eventLayouts.length === 0) {
            return 1;
        }

        return Math.max(...eventLayouts.map(({ lane }) => lane)) + 1;
    }

    getAvailableEventLane(laneEndDates, startDate) {
        const startDateKey = this.parseDateKey(startDate);
        const lane = laneEndDates.findIndex((endDate) => this.parseDateKey(endDate) < startDateKey);

        return lane === -1 ? laneEndDates.length : lane;
    }

    getCalendarRow(date, dateRows) {
        return dateRows.get(this.formatDateKey(date));
    }

    compareEventsForCalendar(eventA, eventB) {
        const durationA = this.getEventDuration(eventA);
        const durationB = this.getEventDuration(eventB);

        if (durationA !== durationB) {
            return durationB - durationA;
        }

        return this.compareEventValue(eventA.start_date, eventB.start_date)
            || this.compareEventValue(this.getLocalizedValue(eventA.name), this.getLocalizedValue(eventB.name));
    }

    getEventDuration(event) {
        return this.getInclusiveDaySpan(event.start_date, event.end_date || event.start_date);
    }

    compareEventValue(valueA, valueB) {
        return String(valueA).localeCompare(String(valueB), this.locale);
    }

    getLocalizedValue(value, sourceLocale = null) {
        if (!value || typeof value !== 'object') {
            return value || '';
        }

        return value[this.locale] || value[sourceLocale] || value.fr || Object.values(value)[0] || '';
    }

    goToPreviousMonth() {
        this.currentDate.setMonth(this.currentDate.getMonth() - 1);
        this.render();
    }

    goToNextMonth() {
        this.currentDate.setMonth(this.currentDate.getMonth() + 1);
        this.render();
    }

    getEventMeta(event) {
        return '';
    }

    formatEventLocation(event) {
        return [
            event.organizer?.name || event.location?.organization,
            event.city || event.location?.city,
            this.getEventCountry(event)
        ].filter(Boolean).join(', ');
    }

    appendEventLocation(container, event, options = {}) {
        const organization = options.includeOrganization === false
            ? ''
            : event.organizer?.name || event.location?.organization;
        const place = this.formatEventLocationPlace(event);

        container.textContent = '';

        if (organization) {
            const organizationElement = document.createElement('span');

            organizationElement.className = 'calendar-event-location-organization';
            organizationElement.textContent = organization;
            container.append(organizationElement);
        }

        if (place) {
            const placeElement = document.createElement('span');

            placeElement.className = 'calendar-event-location-place';
            placeElement.textContent = place;
            container.append(placeElement);
        }
    }

    formatEventDateRange(event) {
        const startDate = this.parseDate(event.start_date);
        const endDate = this.parseDate(event.end_date || event.start_date);

        if (!startDate) {
            return '';
        }

        if (!endDate || event.start_date === (event.end_date || event.start_date)) {
            return this.formatFullDate(startDate);
        }

        return `${this.formatFullDate(startDate)} → ${this.formatFullDate(endDate)}`;
    }

    formatEventDay(event) {
        const date = this.parseDate(event.start_date);

        return date ? this.formatDateDay(date) : '';
    }

    formatEventMonth(event) {
        const date = this.parseDate(event.start_date);

        return date ? this.formatDateMonth(date) : '';
    }

    formatEventYear(event) {
        const date = this.parseDate(event.start_date);

        return date ? this.formatDateYear(date) : '';
    }

    formatWeekday(date) {
        return new Intl.DateTimeFormat(this.locale, { weekday: 'short' }).format(date).replace(/\.$/, '');
    }

    formatTimelineWeekday(date) {
        const weekday = new Intl.DateTimeFormat(this.locale, { weekday: 'long' })
            .format(date)
            .replace(/[^\p{L}\p{N}]/gu, '');

        return [...weekday].slice(0, 3).join('').toLocaleUpperCase(this.locale);
    }

    isWeekend(date) {
        return date.getDay() === 0 || date.getDay() === 6;
    }

    formatDateDay(date) {
        return new Intl.DateTimeFormat(this.locale, { day: '2-digit' }).format(date);
    }

    formatDateMonth(date) {
        return new Intl.DateTimeFormat(this.locale, { month: 'short' }).format(date).replace(/\.$/, '');
    }

    formatDateYear(date) {
        return new Intl.DateTimeFormat(this.locale, { year: 'numeric' }).format(date);
    }

    formatFullDate(date) {
        return new Intl.DateTimeFormat(this.locale, {
            weekday: 'short',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
    }

    parseDate(dateKey) {
        if (!dateKey) {
            return null;
        }

        const [year, month, day] = dateKey.split('-').map(Number);

        return new Date(year, month - 1, day);
    }

    formatMonthTitle(date) {
        return new Intl.DateTimeFormat(this.locale, {
            month: 'long',
            year: 'numeric'
        }).format(date);
    }

    formatDayTitle(date) {
        return new Intl.DateTimeFormat(this.locale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
    }

    formatDateKey(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }
}
